import { BrowserRouter, Routes, Route, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import JSZip from "jszip";

//                                                                
//  MEMORIES BY INES   Application complete
//  Stack : React + Firebase (Auth, Firestore, Storage) + Stripe
//                                                                
//
//  FICHIERS A CREER (structure du projet) :
//  memories-by-ines/
//      public/
//      src/
//          firebase.js            config Firebase
//          App.jsx                routeur principal
//          pages/
//              Landing.jsx        page d'accueil
//              EventPage.jsx      page invite (via QR)
//              Dashboard.jsx      admin dashboard
//              Login.jsx          connexion admin
//              Checkout.jsx       paiement Stripe
//          components/
//              Gallery.jsx
//              AudioRecorder.jsx
//              QRGenerator.jsx
//      functions/                 Firebase Cloud Functions
//          index.js               generation QR + Stripe webhook
//      .env                       cles secretes
//      package.json
//
//                                                                

import { useState, useEffect, useRef } from "react";

import { auth, db, storage, functions } from "./firebase";
import { signInWithEmailAndPassword, signInWithCustomToken, onAuthStateChanged, signOut } from "firebase/auth";
import {
  collection, collectionGroup, addDoc, getDocs, getDoc, setDoc, onSnapshot, doc, updateDoc,
  deleteDoc, query, where, orderBy, serverTimestamp, increment, getCountFromServer,
} from "firebase/firestore";
import { ref, uploadBytes, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";

//     STYLES    
const S = {
  gold: "#C8B89A", goldL: "#A8977A", dark: "#FAF8F5",
  dark2: "#FFFFFF", dark3: "#F0EDE8", cream: "#2C2A25",
  textMid: "#4A4845", textLight: "#6B6864", green: "#4A8A5A",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=Montserrat:wght@300;400;500&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth}
  body{font-family:'Montserrat',sans-serif;background:#FAF8F5;color:#2C2A25;font-size:1rem;font-weight:400;overflow-x:hidden}
  ::-webkit-scrollbar{width:4px}
  ::-webkit-scrollbar-track{background:#F0EDE8}
  ::-webkit-scrollbar-thumb{background:#C8B89A;border-radius:2px}
  input,textarea,select{outline:none}
  button{cursor:pointer;font-family:'Montserrat',sans-serif}
`;

const SITE_DOMAIN = process.env.NODE_ENV === "production" ? "memoriesbyines.com" : "localhost:3000";
// Statuts des documents "events" crees par le parcours de paiement Stripe
// (ConfirmationPage) avant que l'admin ne batisse la page definitive.
const PIPELINE_STATUSES = ["paiement_confirme", "infos_recues", "traite"];
const SITE_ORIGIN = process.env.NODE_ENV === "production" ? `https://${SITE_DOMAIN}` : `http://${SITE_DOMAIN}`;

// Source unique des formules, utilisee par LandingPage, CheckoutPage et le
// modal admin "Nouvel evenement". Les montants doivent rester synchronises
// avec PLAN_PRICES dans functions/index.js (c'est ce dernier qui fait foi
// pour le montant reellement facture par Stripe).
const PLAN_INFO = {
  essentiel: {
    name: "Essentiel", price: 69, popular: false,
    features: ["1 QR code", "Messages vocaux illimités", "Témoignages écrits", "6 mois d'accès"],
  },
  prestige: {
    name: "Prestige", price: 89, popular: false,
    features: ["QR code", "Photos + vidéos illimitées", "Page personnalisable", "Accès illimité"],
  },
  signature: {
    name: "Signature", price: 129, popular: true,
    features: ["QR code premium", "Photos + vidéos illimitées", "Messages vocaux illimités", "Page personnalisable"],
  },
};

// Normalise les accents (é -> e, ç -> c, ...) avant de retirer les caracteres
// non alphanumeriques, sinon un nom accentue produit un slug tronque/casse.
const DIACRITICS_RE = new RegExp("[\\u0300-\\u036f]", "g");
const slugify = (str) =>
  str
    .normalize("NFD").replace(DIACRITICS_RE, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const timeAgo = (ts) => {
  if (!ts) return "À l'instant";
  const diffMin = Math.max(0, Math.round((Date.now() - ts.toDate().getTime()) / 60000));
  if (diffMin < 1) return "À l'instant";
  if (diffMin < 60) return `Il y a ${diffMin} min`;
  if (diffMin < 1440) return `Il y a ${Math.round(diffMin / 60)}h`;
  return ts.toDate().toLocaleDateString("fr-FR");
};

//
//   COMPOSANTS UI
//                                

const Btn = ({ children, variant = "gold", onClick, style = {}, disabled }) => {
  const base = { fontFamily: "'Montserrat',sans-serif", fontSize: "0.6rem", letterSpacing: "0.22em", textTransform: "uppercase", padding: "13px 32px", border: "none", transition: "all 0.25s", cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, ...style };
  const variants = {
    gold: { background: S.gold, color: "#2C2A25", fontWeight: 500 },
    ghost: { background: "transparent", color: S.cream, border: `1px solid ${S.gold}`, padding: "12px 32px" },
    danger: { background: "#9A3A3A", color: "#FFFFFF" },
  };
  return <button style={{ ...base, ...variants[variant] }} onClick={onClick} disabled={disabled}>{children}</button>;
};

const Input = ({ label, ...props }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    {label && <label style={{ fontSize: "0.7rem", letterSpacing: "0.25em", textTransform: "uppercase", color: "#6B6864" }}>{label}</label>}
    <input style={{ background: S.dark3, border: `1px solid rgba(200,184,154,0.3)`, padding: "11px 16px", color: S.cream, fontFamily: "'Montserrat',sans-serif", fontSize: "1rem", fontWeight: 400, borderRadius: 0, width: "100%" }} {...props} />
  </div>
);

const Select = ({ label, children, ...props }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    {label && <label style={{ fontSize: "0.7rem", letterSpacing: "0.25em", textTransform: "uppercase", color: "#6B6864" }}>{label}</label>}
    <select style={{ background: S.dark3, border: `1px solid rgba(200,184,154,0.3)`, padding: "11px 16px", color: S.cream, fontFamily: "'Montserrat',sans-serif", fontSize: "1rem", fontWeight: 400, borderRadius: 0, appearance: "none", width: "100%" }} {...props}>{children}</select>
  </div>
);

const Card = ({ children, style = {}, ...rest }) => (
  <div style={{ background: S.dark2, border: `1px solid rgba(200,184,154,0.2)`, ...style }} {...rest}>{children}</div>
);

const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!checked)}
    disabled={disabled}
    style={{
      width: 40, height: 22, borderRadius: 11, border: "none", padding: 2, flexShrink: 0,
      background: checked ? S.gold : "rgba(200,184,154,0.25)",
      cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.4 : 1, transition: "background 0.2s",
    }}
  >
    <span style={{ display: "block", width: 18, height: 18, borderRadius: "50%", background: S.dark2, transform: checked ? "translateX(18px)" : "translateX(0)", transition: "transform 0.2s" }} />
  </button>
);

// Reutilise par le formulaire de creation et le dashboard (edition apres coup) :
// meme restriction de plan (Prestige & Signature uniquement) aux deux endroits.
const PelliculeToggleField = ({ plan, checked, onChange }) => {
  const allowed = plan === "prestige" || plan === "signature";
  return (
    <div style={{ padding: "14px 16px", background: S.dark3, border: "1px solid rgba(200,184,154,0.15)", opacity: allowed ? 1 : 0.45 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: "0.72rem", color: S.cream, fontWeight: 400 }}>Mode pellicule jetable</span>
          <span style={{ fontSize: "0.48rem", letterSpacing: "0.1em", textTransform: "uppercase", color: S.gold, border: `1px solid ${S.gold}44`, padding: "2px 6px" }}>Prestige & Signature</span>
        </div>
        <Toggle checked={allowed && checked} onChange={onChange} disabled={!allowed} />
      </div>
      <div style={{ fontSize: "0.65rem", color: S.textLight, marginTop: 8, lineHeight: 1.5, fontWeight: 300 }}>
        Chaque invité dispose de 36 photos, comme un appareil jetable. Un compteur s'affiche sur sa page.
      </div>
      {!allowed && (
        <div style={{ fontSize: "0.6rem", color: S.textLight, marginTop: 6, fontStyle: "italic" }}>
          Disponible uniquement avec les formules Prestige et Signature.
        </div>
      )}
    </div>
  );
};

const Badge = ({ type }) => {
  const map = { wedding: ["  Mariage", "#E8C4B0"], birthday: ["  Anniversaire", "#C4D4E8"], corporate: ["  Corporate", "#C4E8C8"], baptism: ["  Baptême", "#E8E0C4"], anniversary: ["  Anniversaire", "#E8D5A3"] };
  const [label, color] = map[type] || ["  Événement", S.gold];
  return <span style={{ fontSize: "0.52rem", letterSpacing: "0.15em", padding: "3px 10px", border: `1px solid ${color}44`, color, textTransform: "uppercase" }}>{label}</span>;
};

const StatusDot = ({ status }) => {
  const map = { live: [S.green, "Live"], upcoming: [S.gold, "À venir"], done: [S.textLight, "Terminé"] };
  const [color, label] = map[status] || [S.textLight, status];
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: "0.58rem", color }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: color, display: "inline-block", animation: status === "live" ? "blink 1.5s infinite" : "none" }} />
      {label}
    </span>
  );
};

const Toast = ({ msg, visible }) => (
  <div style={{ position: "fixed", bottom: 28, right: 28, background: S.dark2, border: `1px solid rgba(200,184,154,0.35)`, borderLeft: `3px solid ${S.gold}`, color: S.cream, padding: "14px 20px", fontSize: "0.72rem", fontWeight: 300, opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(10px)", transition: "all 0.3s", zIndex: 9999, pointerEvents: "none", maxWidth: 280, boxShadow: "0 4px 24px rgba(44,35,24,0.08)" }}>{msg}</div>
);

const Modal = ({ open, onClose, title, subtitle, children, maxWidth = 500 }) => {
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(44,35,24,0.4)", backdropFilter: "blur(8px)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: S.dark2, border: `1px solid rgba(200,184,154,0.2)`, width: "100%", maxWidth, position: "relative", maxHeight: "90vh", overflowY: "auto", animation: "fadeIn 0.3s ease" }}>
        <div style={{ position: "absolute", top: -1, left: 60, right: 60, height: 2, background: `linear-gradient(90deg,transparent,${S.gold},transparent)` }} />
        <div style={{ padding: "32px 32px 0", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.5rem", fontWeight: 300, color: S.cream }}>{title}</div>
            {subtitle && <div style={{ fontSize: "0.62rem", color: S.textLight, marginTop: 4 }}>{subtitle}</div>}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: S.textLight, fontSize: "1.2rem", cursor: "pointer", padding: 4 }}> </button>
        </div>
        <div style={{ padding: "24px 32px 32px" }}>{children}</div>
      </div>
    </div>
  );
};

//                                
//   QR CODE GENERATOR
//                                
const QRCode = ({ slug, code, size = 160 }) => (
  <div style={{ background: S.cream, padding: 12, display: "inline-block", border: `2px solid ${S.gold}`, borderRadius: 2 }}>
    <QRCodeSVG
      value={code ? `${SITE_ORIGIN}/event/${slug}?code=${code}` : `${SITE_ORIGIN}/event/${slug}`}
      size={size}
      bgColor={S.cream}
      fgColor={S.dark}
      level="M"
    />
  </div>
);

//                                
//   AUDIO RECORDER
//                                
const AudioRecorder = ({ onSave }) => {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [saved, setSaved] = useState(false);
  const timerRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const toggle = async () => {
    if (!recording) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mr = new MediaRecorder(stream);
        chunksRef.current = [];
        mr.ondataavailable = e => chunksRef.current.push(e.data);
        mr.onstop = () => {
          const blob = new Blob(chunksRef.current, { type: "audio/webm" });
          onSave && onSave(blob, seconds);
          setSaved(true);
          stream.getTracks().forEach(t => t.stop());
        };
        mr.start();
        mediaRef.current = mr;
        setRecording(true);
        setSeconds(0);
        setSaved(false);
        timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
      } catch {
        alert("Accès au microphone refusé. Autorisez le micro dans votre navigateur.");
      }
    } else {
      mediaRef.current?.stop();
      clearInterval(timerRef.current);
      setRecording(false);
    }
  };

  const fmt = s => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  const bars = Array.from({ length: 12 });

  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ display: "flex", justifyContent: "center", gap: 3, height: 40, alignItems: "center", marginBottom: 20 }}>
        {bars.map((_, i) => (
          <div key={i} style={{
            width: 3, borderRadius: 2, background: S.gold, opacity: recording ? 0.8 : 0.2,
            height: recording ? `${8 + Math.sin(Date.now() / 200 + i) * 16}px` : `${4 + (i % 3) * 4}px`,
            transition: "height 0.1s", animation: recording ? `waveAnim ${0.6 + i * 0.05}s ease-in-out infinite alternate` : "none"
          }} />
        ))}
      </div>
      <button onClick={toggle} style={{
        width: 80, height: 80, borderRadius: "50%",
        background: recording ? "rgba(200,184,154,0.15)" : S.dark3,
        border: `2px solid ${recording ? S.gold : "rgba(200,184,154,0.3)"}`,
        color: S.cream, fontSize: "1.8rem", cursor: "pointer", transition: "all 0.3s",
        animation: recording ? "recordPulse 1.2s ease-in-out infinite" : "none",
        display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px"
      }}>
        {recording ? " " : " "}
      </button>
      <div style={{ fontSize: "0.65rem", letterSpacing: "0.2em", color: recording ? S.gold : S.textLight }}>
        {saved ? `  Enregistrement prêt (${fmt(seconds)})` : recording ? `Enregistrement   ${fmt(seconds)}` : "Appuyez pour commencer"}
      </div>
    </div>
  );
};

//                                
//   UPLOAD ZONE
//                                
const UploadZone = ({ onFiles, accept = "image/*,video/*" }) => {
  const [drag, setDrag] = useState(false);
  const ref = useRef();
  const handle = files => {
    const valid = Array.from(files).filter(f => f.type.startsWith("image/") || f.type.startsWith("video/"));
    if (valid.length) onFiles(valid);
  };
  return (
    <div
      style={{ border: `1px dashed rgba(200,184,154,${drag ? 0.8 : 0.35})`, background: drag ? "rgba(200,184,154,0.06)" : "rgba(200,184,154,0.02)", padding: "48px 32px", textAlign: "center", cursor: "pointer", transition: "all 0.3s" }}
      onClick={() => ref.current.click()}
      onDragOver={e => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={e => { e.preventDefault(); setDrag(false); handle(e.dataTransfer.files); }}
    >
      <div style={{ fontSize: "2rem", marginBottom: 12 }}> </div>
      <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.3rem", color: S.cream, marginBottom: 8 }}>
        Déposez vos photos & vidéos
      </div>
      <div style={{ fontSize: "1rem", color: "#6B6864", fontWeight: 400, lineHeight: 1.8 }}>
        JPG, PNG, HEIC, MP4, MOV   Jusqu&apos;à 10 fichiers
      </div>
      <input ref={ref} type="file" accept={accept} multiple style={{ display: "none" }} onChange={e => handle(e.target.files)} />
    </div>
  );
};

//                                                    
//   PAGE : LOGIN ADMIN
//                                                    
const LoginPage = ({ onLogin }) => {
  const [email, setEmail] = useState("ines@memoriesbyines.com");
  const [pass, setPass] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!pass.trim()) { setErr("Mot de passe requis"); return; }
    setLoading(true); setErr("");
    try {

      await signInWithEmailAndPassword(auth, email, pass);
      onLogin();
    } catch(e) {
      setErr("Email ou mot de passe incorrect");
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: S.dark, padding: 24 }}>
      <div style={{ width: "100%", maxWidth: 400, textAlign: "center" }}>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "2rem", fontWeight: 400, color: S.cream, marginBottom: 8 }}>
          Memories <em style={{ fontStyle: "italic", color: S.gold }}>by Inès</em>
        </div>
        <div style={{ fontSize: "0.58rem", letterSpacing: "0.3em", textTransform: "uppercase", color: S.textLight, marginBottom: 48 }}>Espace administrateur</div>
        <Card style={{ padding: 40, textAlign: "left", position: "relative" }}>
          <div style={{ position: "absolute", top: -1, left: 40, right: 40, height: 2, background: `linear-gradient(90deg,transparent,${S.gold},transparent)` }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 24 }}>
            <Input label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="votre@email.fr" />
            <Input label="Mot de passe" type="password" value={pass} onChange={e => setPass(e.target.value)} placeholder="        " onKeyDown={e => e.key === "Enter" && submit()} />
          </div>
          {err && <div style={{ fontSize: "0.7rem", color: "#E88A8A", marginBottom: 16 }}>{err}</div>}
          <Btn onClick={submit} disabled={loading} style={{ width: "100%", padding: 14 }}>
            {loading ? "Connexion..." : "Se connecter  "}
          </Btn>
          <div style={{ fontSize: "0.62rem", color: S.textLight, textAlign: "center", marginTop: 16, fontWeight: 300 }}>
            Mot de passe oublié ? <span style={{ color: S.gold, cursor: "pointer" }}>Réinitialiser</span>
          </div>
        </Card>
        <div style={{ fontSize: "0.58rem", color: S.textLight, marginTop: 24, fontWeight: 300 }}>
          
        </div>
      </div>
    </div>
  );
};

const downloadAudiosZip = async (audios, zipName) => {
  const withUrl = audios.filter(a => a.url);
  if (!withUrl.length) return;
  const zip = new JSZip();
  await Promise.all(withUrl.map(async (a, i) => {
    const res = await fetch(a.url);
    const blob = await res.blob();
    const safe = (a.author || "Anonyme").replace(/[^a-zA-Z0-9_À-ɏ\s-]/g, "_").trim();
    zip.file(`${String(i + 1).padStart(2, "0")}_${safe}.webm`, blob);
  }));
  const blob = await zip.generateAsync({ type: "blob" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${zipName}.zip`;
  link.click();
  URL.revokeObjectURL(link.href);
};

// Le nom de fichier original n'est pas conserve dans Firestore (seule l'URL
// Storage l'est) : on retrouve l'extension a partir du type MIME du blob.
const MIME_EXT = {
  "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp", "image/gif": "gif",
  "video/mp4": "mp4", "video/quicktime": "mov", "video/webm": "webm", "audio/webm": "webm",
};
const extFromBlob = (blob, fallback) => MIME_EXT[blob.type] || fallback;

// ZIP complet d'un evenement : photos, videos, messages vocaux + un fichier
// texte pour les temoignages ecrits (stockes sans `url`, avec juste `text`).
const downloadEventZip = async (event, media, audio, notify) => {
  const photos = media.filter(m => m.type !== "video" && m.url);
  const videos = media.filter(m => m.type === "video" && m.url);
  const voiceMessages = audio.filter(a => a.url);
  const testimonials = audio.filter(a => a.text && !a.url);

  if (!photos.length && !videos.length && !voiceMessages.length && !testimonials.length) {
    notify("Aucun fichier à télécharger pour cet événement");
    return false;
  }

  const zip = new JSZip();
  let failedCount = 0;
  // Un fichier corrompu/inaccessible ne doit pas faire echouer tout le ZIP :
  // on isole chaque telechargement avec allSettled plutot que Promise.all.
  const addFiles = async (items, folderName, fallbackExt) => {
    if (!items.length) return;
    const folder = zip.folder(folderName);
    const results = await Promise.allSettled(items.map(async (item, i) => {
      const res = await fetch(item.url);
      if (!res.ok) throw new Error(`HTTP ${res.status} pour ${item.url}`);
      const blob = await res.blob();
      const safe = (item.author || "Anonyme").replace(/[^a-zA-Z0-9_À-ɏ\s-]/g, "_").trim();
      folder.file(`${String(i + 1).padStart(2, "0")}_${safe}.${extFromBlob(blob, fallbackExt)}`, blob);
    }));
    results.forEach(r => {
      if (r.status === "rejected") {
        failedCount++;
        console.error(`[downloadEventZip] échec sur "${folderName}" :`, r.reason);
      }
    });
  };

  await Promise.all([
    addFiles(photos, "Photos", "jpg"),
    addFiles(videos, "Vidéos", "mp4"),
    addFiles(voiceMessages, "Messages vocaux", "webm"),
  ]);

  if (testimonials.length) {
    const body = testimonials.map(t => {
      const date = t.createdAt?.toDate ? t.createdAt.toDate().toLocaleDateString("fr-FR") : "";
      return `${t.author || "Anonyme"}${date ? ` (${date})` : ""} :\n${t.text}`;
    }).join("\n\n---\n\n");
    zip.file("temoignages.txt", body);
  }

  const totalFiles = photos.length + videos.length + voiceMessages.length;
  if (failedCount > 0 && failedCount === totalFiles) {
    notify("Échec du téléchargement : impossible d'accéder aux fichiers (vérifiez la console)");
    return false;
  }
  if (failedCount > 0) {
    notify(`${failedCount} fichier(s) n'ont pas pu être téléchargés, le reste est inclus dans le ZIP`);
  }

  const blob = await zip.generateAsync({ type: "blob" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${slugify(event.name) || event.id}.zip`;
  link.click();
  URL.revokeObjectURL(link.href);
  return true;
};

//
//   PAGE : EVENT (vue invite via QR code)
//
const EventPage = ({ event, onBack, role = "owner", guestName = "", authorKey = "" }) => {
  const isGuest = role === "guest";
  // La formule determine ce qui est possible sur CET evenement, quel que
  // soit qui essaie d'uploader (invite, maries ou admin) - cf. les memes
  // garde-fous cote serveur dans firestore.rules (planAllowsMedia/Audio).
  const planAllowsPhotos = event.plan !== "essentiel";
  const planAllowsAudio = event.plan !== "prestige";
  const tabs = [
    planAllowsPhotos && ["photos", " ", "Photos & Vidéos"],
    planAllowsAudio && ["audio", " ", "Messages vocaux"],
    !isGuest && planAllowsPhotos && ["album", " ", "Album"],
  ].filter(Boolean);
  const [tab, setTab] = useState(planAllowsPhotos ? "photos" : "audio");
  useEffect(() => {
    if (!tabs.some(([id]) => id === tab)) setTab(tabs[0]?.[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.plan, isGuest]);
  const [photos, setPhotos] = useState([]);
  const [audios, setAudios] = useState([]);
  const [authorName, setAuthorName] = useState(isGuest ? guestName : "");
  const [caption, setCaption] = useState("");
  const [audioAuthor, setAudioAuthor] = useState(isGuest ? guestName : "");
  const [textMessage, setTextMessage] = useState("");
  const [toast, setToast] = useState({ msg: "", visible: false });
  const [lightbox, setLightbox] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [zipLoading, setZipLoading] = useState(false);
  const [pelliculeCount, setPelliculeCount] = useState(null);
  const pelliculeActive = isGuest && event.pelliculeJetable === true;

  useEffect(() => {
    const mediaQuery = isGuest
      ? query(collection(db, "events", event.id, "media"), where("authorKey", "==", authorKey), orderBy("createdAt", "desc"))
      : query(collection(db, "events", event.id, "media"), orderBy("createdAt", "desc"));
    const unsubMedia = onSnapshot(
      mediaQuery,
      snap => setPhotos(snap.docs.map(d => ({ id: d.id, ...d.data(), time: timeAgo(d.data().createdAt) })))
    );
    const unsubAudio = onSnapshot(
      query(collection(db, "events", event.id, "audio"), orderBy("createdAt", "desc")),
      snap => setAudios(snap.docs.map(d => ({ id: d.id, ...d.data(), time: timeAgo(d.data().createdAt) })))
    );
    return () => { unsubMedia(); unsubAudio(); };
  }, [event.id, isGuest, authorKey]);

  // Mode pellicule jetable : un compteur par appareil, identifie par un id
  // genere au premier scan et garde en localStorage (pas d'auth invite
  // dediee - si l'invite vide son cache, il "recharge" une pellicule).
  useEffect(() => {
    if (!pelliculeActive) { setPelliculeCount(null); return; }
    let deviceId = localStorage.getItem("pelliculeDeviceId");
    if (!deviceId) {
      deviceId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem("pelliculeDeviceId", deviceId);
    }
    const pelliculeRef = doc(db, "events", event.id, "pellicules", deviceId);
    let active = true;
    (async () => {
      try {
        const snap = await getDoc(pelliculeRef);
        if (!snap.exists()) await setDoc(pelliculeRef, { photosRestantes: 36, createdAt: serverTimestamp() });
      } catch (e) { /* l'onSnapshot ci-dessous reste la source de verite affichee */ }
    })();
    const unsub = onSnapshot(pelliculeRef, snap => {
      if (active) setPelliculeCount(snap.exists() ? snap.data().photosRestantes : 36);
    });
    return () => { active = false; unsub(); };
  }, [pelliculeActive, event.id]);

  const notify = msg => {
    setToast({ msg, visible: true });
    setTimeout(() => setToast(t => ({ ...t, visible: false })), 3000);
  };

  const handleFiles = async (files) => {
    setUploading(true);
    try {
      let done = 0;
      for (const file of files) {
        const isVideo = file.type.startsWith("video");
        const storageRef = ref(storage, `events/${event.id}/${Date.now()}_${file.name}`);
        const task = uploadBytesResumable(storageRef, file);
        const filesDoneSoFar = done;
        await new Promise((resolve, reject) => {
          task.on(
            "state_changed",
            snap => setProgress(Math.round(((filesDoneSoFar + snap.bytesTransferred / snap.totalBytes) / files.length) * 100)),
            reject,
            resolve
          );
        });
        const url = await getDownloadURL(task.snapshot.ref);
        await addDoc(collection(db, "events", event.id, "media"), {
          type: isVideo ? "video" : "photo",
          url, author: authorName || "Anonyme", authorKey: isGuest ? authorKey : "", caption,
          createdAt: serverTimestamp(),
        });
        await updateDoc(doc(db, "events", event.id), { [isVideo ? "videos" : "photos"]: increment(1) });
        if (!isVideo && pelliculeActive) {
          const deviceId = localStorage.getItem("pelliculeDeviceId");
          if (deviceId) {
            await updateDoc(doc(db, "events", event.id, "pellicules", deviceId), { photosRestantes: increment(-1) }).catch(() => {});
          }
        }
        done++;
      }
      notify(`${files.length} fichier${files.length > 1 ? "s" : ""} partagé${files.length > 1 ? "s" : ""}`);
    } catch (e) {
      notify("Échec de l'envoi, réessayez");
    }
    setUploading(false);
    setProgress(0);
    if (!isGuest) setAuthorName("");
    setCaption("");
  };

  const deleteMedia = async (m) => {
    try {
      await deleteDoc(doc(db, "events", event.id, "media", m.id));
      await updateDoc(doc(db, "events", event.id), { [m.type === "video" ? "videos" : "photos"]: increment(-1) });
      notify("Souvenir supprimé");
    } catch (e) {
      notify("Échec de la suppression");
    }
  };

  const deleteAudio = async (a) => {
    try {
      await deleteDoc(doc(db, "events", event.id, "audio", a.id));
      await updateDoc(doc(db, "events", event.id), { audios: increment(-1) });
      notify("Message vocal supprimé");
    } catch (e) {
      notify("Échec de la suppression");
    }
  };

  const handleAudio = async (blob, secs) => {
    const fmt = s => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
    try {
      const storageRef = ref(storage, `events/${event.id}/audio/${Date.now()}.webm`);
      await uploadBytes(storageRef, blob);
      const url = await getDownloadURL(storageRef);
      await addDoc(collection(db, "events", event.id, "audio"), {
        url, author: audioAuthor || "Anonyme", authorKey: isGuest ? authorKey : "", duration: fmt(secs), createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, "events", event.id), { audios: increment(1) });
      notify("Message vocal envoyé avec amour");
    } catch (e) {
      notify("Échec de l'envoi, réessayez");
    }
    if (!isGuest) setAudioAuthor("");
  };

  const handleTextMessage = async () => {
    if (!textMessage.trim()) return;
    try {
      await addDoc(collection(db, "events", event.id, "audio"), {
        text: textMessage.trim(), author: audioAuthor || "Anonyme", authorKey: isGuest ? authorKey : "", createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, "events", event.id), { audios: increment(1) });
      notify("Témoignage envoyé avec amour");
      setTextMessage("");
      if (!isGuest) setAudioAuthor("");
    } catch (e) {
      notify("Échec de l'envoi, réessayez");
    }
  };

  const typeColors = { wedding: "#C9A96E", birthday: "#C4D4E8", corporate: "#C4E8C8", baptism: "#E8C4B0" };
  const accent = typeColors[event.type] || S.gold;

  return (
    <div style={{ minHeight: "100vh", background: S.dark }}>
      <style>{css}
        {`@keyframes blink{0%,100%{opacity:1}50%{opacity:0.3}}
          @keyframes recordPulse{0%,100%{box-shadow:0 0 0 0 rgba(200,184,154,0.4)}50%{box-shadow:0 0 0 18px rgba(200,184,154,0)}}
          @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
          @keyframes waveAnim{from{height:4px}to{height:28px}}`}
      </style>
      <Toast msg={toast.msg} visible={toast.visible} />

      {/* Lightbox */}
      {lightbox && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(11,9,6,0.97)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setLightbox(null)}>
          <button style={{ position: "absolute", top: 20, right: 20, background: "none", border: "none", color: "#FAF8F5", fontSize: "1.5rem", cursor: "pointer" }}> </button>
          {lightbox.url ? <img src={lightbox.url} style={{ maxWidth: "90vw", maxHeight: "85vh", objectFit: "contain" }} alt="" /> :
            <div style={{ background: lightbox.bg, width: 300, height: 300, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "5rem" }}>{lightbox.emoji}</div>}
        </div>
      )}

      {/* Hero */}
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "80px 24px", position: "relative", overflow: "hidden" }}>
        {event.coverPhoto && (
          <>
            <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${event.coverPhoto})`, backgroundSize: "cover", backgroundPosition: "center" }} />
            <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)" }} />
          </>
        )}
        <div style={{ position: "absolute", inset: 0, background: `radial-gradient(ellipse 60% 50% at 50% 40%, ${accent}18 0%, transparent 60%)` }} />
        {[500, 750, 1050].map((s, i) => (
          <div key={i} style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: s, height: s, borderRadius: "50%", border: `1px solid ${accent}12`, animation: `blink ${3 + i}s ease-in-out infinite`, animationDelay: `${i}s`, pointerEvents: "none" }} />
        ))}
        {onBack && <button onClick={onBack} style={{ position: "absolute", top: 20, left: 20, background: "none", border: `1px solid rgba(200,184,154,0.2)`, color: S.textLight, padding: "8px 16px", fontSize: "0.6rem", letterSpacing: "0.15em", cursor: "pointer", zIndex: 10 }}>  Retour</button>}
        <div style={{ position: "relative", zIndex: 1 }}>
          <div style={{ fontSize: "0.58rem", letterSpacing: "0.4em", textTransform: "uppercase", color: event.coverPhoto ? "rgba(250,248,245,0.9)" : accent, border: `1px solid ${event.coverPhoto ? "rgba(250,248,245,0.35)" : accent + "44"}`, padding: "7px 18px", marginBottom: 36, display: "inline-block" }}>  Partagez vos souvenirs  </div>
          <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(3rem,10vw,6.5rem)", fontWeight: 300, fontStyle: "italic", lineHeight: 1, color: event.coverPhoto ? "#FAF8F5" : S.cream, marginBottom: 16 }}>{event.name}</h1>
          <div style={{ fontSize: "0.85rem", letterSpacing: "0.3em", textTransform: "uppercase", color: event.coverPhoto ? "rgba(250,248,245,0.9)" : "#6B6864", marginBottom: 8 }}>{event.date}</div>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.25rem", fontStyle: "italic", color: event.coverPhoto ? "rgba(250,248,245,0.92)" : S.textMid, marginBottom: 16 }}>{event.location}</div>
          {event.welcomeMsg && <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.4rem", fontWeight: 500, fontStyle: "italic", color: event.coverPhoto ? "rgba(250,248,245,0.92)" : `${accent}dd`, maxWidth: 560, margin: "0 auto 48px", lineHeight: 1.7 }}>"{event.welcomeMsg}"</div>}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: "0.55rem", letterSpacing: "0.3em", textTransform: "uppercase", color: S.textLight }}>Défiler</span>
            <div style={{ width: 1, height: 50, background: `linear-gradient(to bottom,${accent},transparent)` }} />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ position: "sticky", top: 0, zIndex: 50, background: "rgba(250,248,245,0.96)", backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(200,184,154,0.25)" }}>
        <div style={{ display: "flex", maxWidth: 600, margin: "0 auto" }}>
          {tabs.map(([id, icon, label]) => (
            <button key={id} onClick={() => setTab(id)} style={{ flex: 1, padding: "17px 12px", background: "none", border: "none", color: tab === id ? accent : "#6B6864", fontSize: "0.72rem", letterSpacing: "0.2em", textTransform: "uppercase", cursor: "pointer", borderBottom: tab === id ? `2px solid ${accent}` : "2px solid transparent", transition: "all 0.3s", display: "flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
              {icon} {label}
            </button>
          ))}
        </div>
      </div>

      {/* PANEL: Photos */}
      {tab === "photos" && (
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "60px 24px" }}>
          <div style={{ fontSize: "0.72rem", letterSpacing: "0.3em", textTransform: "uppercase", color: "#6B6864", marginBottom: 10, textAlign: "center" }}>Partagez</div>
          <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(1.8rem,4vw,2.6rem)", fontWeight: 400, textAlign: "center", marginBottom: 40, color: "#2C2A25" }}>
            Vos photos & vidéos
          </h2>
          {pelliculeActive && pelliculeCount !== null && (
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontStyle: "italic", fontSize: "2.4rem", color: accent, lineHeight: 1 }}>{Math.max(pelliculeCount, 0)}</div>
              <div style={{ fontSize: "0.6rem", letterSpacing: "0.2em", textTransform: "uppercase", color: "#6B6864", marginTop: 4 }}>
                photo{pelliculeCount > 1 ? "s" : ""} restante{pelliculeCount > 1 ? "s" : ""} — pellicule jetable
              </div>
            </div>
          )}
          <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 160 }}><Input label="Votre prénom" value={authorName} onChange={e => setAuthorName(e.target.value)} placeholder="Sophie, Antoine..." disabled={isGuest} /></div>
            <div style={{ flex: 2, minWidth: 200 }}><Input label="Légende (optionnel)" value={caption} onChange={e => setCaption(e.target.value)} placeholder="Un mot pour ce moment..." /></div>
          </div>
          {pelliculeActive && pelliculeCount <= 0 ? (
            <div style={{ border: "1px dashed rgba(200,184,154,0.35)", background: "rgba(200,184,154,0.02)", padding: "48px 32px", textAlign: "center" }}>
              <div style={{ fontSize: "2rem", marginBottom: 12 }}> </div>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontStyle: "italic", fontSize: "1.3rem", color: S.cream }}>
                Pellicule terminée, merci d'avoir capturé ces souvenirs !
              </div>
            </div>
          ) : (
            <UploadZone onFiles={handleFiles} />
          )}
          {uploading && (
            <div style={{ marginTop: 16 }}>
              <div style={{ height: 2, background: `rgba(200,184,154,0.15)`, borderRadius: 1 }}>
                <div style={{ height: "100%", background: accent, width: `${progress}%`, transition: "width 0.2s", borderRadius: 1 }} />
              </div>
              <div style={{ fontSize: "0.9rem", color: "#4A4845", marginTop: 6, textAlign: "center" }}>Envoi en cours... {Math.round(progress)}%</div>
            </div>
          )}
          {photos.length > 0 && (
            <>
              <div style={{ height: 1, background: "rgba(200,184,154,0.1)", margin: "48px 0 32px", display: "flex", alignItems: "center", gap: 16 }}>
                <span style={{ background: S.dark, padding: "0 16px", fontSize: "0.75rem", color: accent, letterSpacing: "0.2em" }}>  {isGuest ? "VOS SOUVENIRS" : "GALERIE COLLABORATIVE"}  </span>
              </div>
              <div style={{ columns: window.innerWidth < 600 ? 2 : 3, columnGap: 4 }}>
                {photos.map(m => (
                  <div key={m.id} onClick={() => setLightbox(m)} style={{ breakInside: "avoid", marginBottom: 4, position: "relative", overflow: "hidden", cursor: "pointer" }}>
                    {m.url ? <img src={m.url} style={{ width: "100%", display: "block", objectFit: "cover" }} alt="" /> :
                      <div style={{ background: m.bg, aspectRatio: "1", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2.5rem" }}>{m.emoji}</div>}
                    <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top,rgba(11,9,6,0.7) 0%,transparent 50%)", opacity: 0, transition: "opacity 0.3s" }}
                      onMouseEnter={e => e.currentTarget.style.opacity = 1}
                      onMouseLeave={e => e.currentTarget.style.opacity = 0}>
                      <div style={{ position: "absolute", bottom: 8, left: 8, fontSize: "0.6rem", color: "#FAF8F5" }}>{m.author}</div>
                    </div>
                    {m.type === "video" && <div style={{ position: "absolute", top: 8, right: 8, background: "rgba(0,0,0,0.6)", color: "#FAF8F5", fontSize: "0.6rem", padding: "2px 6px" }}>  Vidéo</div>}
                    {isGuest && m.authorKey === authorKey && (
                      <button
                        onClick={e => { e.stopPropagation(); deleteMedia(m); }}
                        style={{ position: "absolute", top: 6, left: 6, width: 22, height: 22, borderRadius: "50%", background: "rgba(11,9,6,0.7)", color: "#FAF8F5", border: "1px solid rgba(255,255,255,0.35)", fontSize: "0.7rem", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                        title="Supprimer"
                      >×</button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* PANEL: Audio */}
      {tab === "audio" && (
        <div style={{ maxWidth: 680, margin: "0 auto", padding: "60px 24px" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div style={{ fontSize: "0.72rem", letterSpacing: "0.3em", textTransform: "uppercase", color: "#6B6864", marginBottom: 10 }}>Livre d'or audio</div>
            <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(1.8rem,4vw,2.6rem)", fontWeight: 400, color: "#2C2A25" }}>Laissez un message vocal</h2>
          </div>
          <Card style={{ padding: 40, textAlign: "center", position: "relative", marginBottom: 32 }}>
            <div style={{ position: "absolute", top: -1, left: "30%", right: "30%", height: 2, background: `linear-gradient(90deg,transparent,${accent},transparent)` }} />
            <AudioRecorder onSave={handleAudio} />
            <div style={{ marginTop: 24 }}>
              <Input label="Votre prénom" value={audioAuthor} onChange={e => setAudioAuthor(e.target.value)} placeholder="Votre prénom..." style={{ textAlign: "center" }} disabled={isGuest} />
            </div>
          </Card>
          {event.plan === "essentiel" && (
            <Card style={{ padding: 32, marginBottom: 32 }}>
              <div style={{ fontSize: "1rem", fontWeight: 600, color: "#2C2A25", marginBottom: 16, textAlign: "center" }}>Ou laissez un témoignage écrit</div>
              <textarea
                value={textMessage}
                onChange={e => setTextMessage(e.target.value)}
                placeholder="Écrivez quelques mots..."
                rows={3}
                style={{ background: S.dark3, border: "1px solid rgba(200,184,154,0.15)", padding: "11px 16px", color: S.cream, fontFamily: "'Montserrat',sans-serif", fontSize: "1rem", fontWeight: 400, width: "100%", resize: "vertical" }}
              />
              <Btn onClick={handleTextMessage} style={{ width: "100%", padding: 13, marginTop: 16 }}>Envoyer le témoignage</Btn>
            </Card>
          )}
          {audios.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <div style={{ fontSize: "0.85rem", letterSpacing: "0.2em", textTransform: "uppercase", color: "#4A4845", marginBottom: 12, textAlign: "center" }}>  Messages reçus ({audios.length})  </div>
              {audios.map(a => (
                <AudioRow
                  key={a.id}
                  audio={a}
                  accent={accent}
                  canDelete={isGuest && a.authorKey === authorKey}
                  onDelete={() => deleteAudio(a)}
                />
              ))}
            </div>
          )}
          {!isGuest && audios.some(a => a.url) && (
            <div style={{ textAlign: "center", marginTop: 28 }}>
              <Btn
                disabled={zipLoading}
                onClick={async () => {
                  setZipLoading(true);
                  try { await downloadAudiosZip(audios, `${event.name || "messages"}_vocaux`); }
                  catch { notify("Échec du téléchargement"); }
                  finally { setZipLoading(false); }
                }}
                style={{ padding: "10px 28px" }}
              >
                {zipLoading ? "Préparation du ZIP..." : "Télécharger tous les audios (ZIP)"}
              </Btn>
            </div>
          )}
        </div>
      )}

      {/* PANEL: Album */}
      {tab === "album" && (
        <div style={{ maxWidth: 680, margin: "0 auto", padding: "60px 24px", textAlign: "center" }}>
          <div style={{ fontSize: "0.72rem", letterSpacing: "0.3em", textTransform: "uppercase", color: "#6B6864", marginBottom: 10 }}>Votre album</div>
          <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(1.8rem,4vw,2.8rem)", fontWeight: 400, color: "#2C2A25", marginBottom: 48 }}>Tous vos souvenirs <em style={{ fontStyle: "italic", color: S.goldL }}>réunis</em></h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 2, marginBottom: 48 }}>
            {[["Photos", photos.filter(m => m.type === "photo").length], ["Vidéos", photos.filter(m => m.type === "video").length], ["Messages", audios.length]].map(([l, v]) => (
              <div key={l} style={{ background: S.dark2, padding: "28px 16px", borderTop: `1px solid ${accent}44` }}>
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "2.5rem", color: S.cream, fontWeight: 300, fontStyle: "italic" }}>{v}</div>
                <div style={{ fontSize: "0.82rem", letterSpacing: "0.15em", textTransform: "uppercase", color: "#4A4845", marginTop: 6, fontWeight: 500 }}>{l}</div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: "1rem", color: "#4A4845", lineHeight: 1.9, fontWeight: 400, marginBottom: 40 }}>
            L&apos;album complet sera disponible après l&apos;événement. Vous serez notifié(e) par email avec un lien de téléchargement.
          </p>
          <Btn onClick={() => notify("Notification activée  ")} style={{ marginBottom: 12 }}>Me notifier quand l&apos;album est prêt</Btn>
          <br />
          <Btn variant="ghost" onClick={() => setTab("photos")} style={{ marginTop: 12 }}>Ajouter mes photos</Btn>
        </div>
      )}

      {/* Footer */}
      <div style={{ borderTop: "1px solid rgba(200,184,154,0.1)", padding: "28px 24px", textAlign: "center" }}>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "0.9rem", fontWeight: 300, color: S.textLight }}>
          Memories <span style={{ fontStyle: "italic", color: accent }}>by Inès</span>
        </div>
      </div>
    </div>
  );
};

const AudioRow = ({ audio, accent, canDelete, onDelete }) => {
  const [playing, setPlaying] = useState(false);
  const [prog, setProg] = useState(0);
  const audioRef = useRef(null);

  const play = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) { el.pause(); return; }
    el.play();
  };

  if (audio.text) {
    return (
      <div style={{ background: S.dark2, padding: "18px 20px", display: "flex", alignItems: "flex-start", gap: 14, border: "1px solid rgba(200,184,154,0.08)" }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1rem", fontStyle: "italic", color: S.cream, lineHeight: 1.6 }}>"{audio.text}"</div>
          <div style={{ fontSize: "0.9rem", color: "#4A4845", marginTop: 8, fontWeight: 400 }}>{audio.author} · {audio.time}</div>
        </div>
        {canDelete && (
          <button
            onClick={onDelete}
            style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(154,90,90,0.1)", color: "#9A5A5A", border: "1px solid rgba(154,90,90,0.2)", fontSize: "0.7rem", cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
            title="Supprimer"
          >×</button>
        )}
      </div>
    );
  }

  return (
    <div style={{ background: S.dark2, padding: "18px 20px", display: "flex", alignItems: "center", gap: 14, border: "1px solid rgba(200,184,154,0.08)" }}>
      <audio
        ref={audioRef}
        src={audio.url}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setProg(0); }}
        onTimeUpdate={e => setProg(e.target.duration ? (e.target.currentTime / e.target.duration) * 100 : 0)}
      />
      <button onClick={play} style={{ width: 40, height: 40, borderRadius: "50%", background: `rgba(200,184,154,0.15)`, border: `1px solid ${S.gold}`, color: "#2C2A25", fontSize: "0.85rem", cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {playing ? " " : " "}
      </button>
      <div style={{ flex: 1 }}>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.1rem", fontStyle: "italic", color: "#2C2A25" }}>{audio.author}</div>
        <div style={{ fontSize: "0.9rem", color: "#4A4845" }}>{audio.time}</div>
        <div style={{ height: 2, background: "rgba(200,184,154,0.12)", marginTop: 8, position: "relative" }}>
          <div style={{ position: "absolute", top: 0, left: 0, height: "100%", background: accent, width: `${prog}%`, transition: "width 0.05s" }} />
        </div>
      </div>
      <div style={{ fontSize: "0.9rem", color: "#4A4845", flexShrink: 0 }}>{audio.duration}</div>
      {canDelete && (
        <button
          onClick={onDelete}
          style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(154,90,90,0.1)", color: "#9A5A5A", border: "1px solid rgba(154,90,90,0.2)", fontSize: "0.7rem", cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
          title="Supprimer"
        >×</button>
      )}
    </div>
  );
};

//                                                    
//   PAGE : PAIEMENT STRIPE (Checkout)
//                                                    
const CheckoutPage = ({ plan, onBack }) => {
  const [searchParams] = useSearchParams();
  const status = searchParams.get("status");
  const [selectedPlan, setSelectedPlan] = useState(searchParams.get("plan") || plan || "signature");
  const p = PLAN_INFO[selectedPlan] || PLAN_INFO.signature;

  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const pay = async () => {
    setLoading(true);
    setError("");
    try {
      const createSession = httpsCallable(functions, "createCheckoutSession");
      const { data } = await createSession({ plan: selectedPlan, email: form.email, origin: window.location.origin });
      if (!data?.url) throw new Error("URL Stripe manquante");
      window.location.href = data.url;
    } catch (e) {
      setError("Le paiement n'a pas pu démarrer, réessayez.");
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: S.dark, padding: "60px 24px", display: "flex", alignItems: "flex-start", justifyContent: "center" }}>
      <div style={{ width: "100%", maxWidth: 800, display: "grid", gridTemplateColumns: window.innerWidth > 700 ? "1fr 1fr" : "1fr", gap: 24 }}>
        {/* Recapitulatif */}
        <Card style={{ padding: 36 }}>
          <button onClick={onBack} style={{ background: "none", border: "none", color: S.textLight, fontSize: "0.65rem", letterSpacing: "0.2em", marginBottom: 24, cursor: "pointer" }}>  Retour</button>
          <div style={{ marginBottom: 20 }}>
            <Select label="Choisissez votre formule" value={selectedPlan} onChange={e => setSelectedPlan(e.target.value)}>
              <option value="essentiel">Essentiel — 69 €</option>
              <option value="prestige">Prestige — 89 €</option>
              <option value="signature">Signature — 129 €</option>
            </Select>
          </div>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "2rem", fontWeight: 300, fontStyle: "italic", color: S.cream, marginBottom: 4 }}>{p.name}</div>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "3rem", fontWeight: 300, fontStyle: "italic", color: S.gold }}>{p.price} €</div>
          <div style={{ fontSize: "0.9rem", color: "#6B6864", marginBottom: 28 }}>par événement — paiement unique</div>
          <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 10 }}>
            {p.features.map(f => (
              <li key={f} style={{ fontSize: "1rem", color: "#4A4845", fontWeight: 400, display: "flex", gap: 10, alignItems: "center" }}>
                <span style={{ color: S.gold }}> </span> {f}
              </li>
            ))}
          </ul>
          <div style={{ marginTop: 28, padding: 16, border: "1px solid rgba(200,184,154,0.12)", fontSize: "0.9rem", color: "#6B6864", lineHeight: 1.7 }}>
              Paiement sécurisé par Stripe.<br />Vous serez redirigé(e) vers la page de paiement Stripe.
          </div>
        </Card>
        {/* Formulaire */}
        <Card style={{ padding: 36 }}>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.4rem", fontWeight: 300, fontStyle: "italic", color: S.cream, marginBottom: 28 }}>Vos coordonnées</div>
          {status === "cancelled" && (
            <div style={{ fontSize: "0.7rem", color: "#E88A8A", marginBottom: 16 }}>Paiement annulé, vous pouvez réessayer.</div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Input label="Nom complet" value={form.name} onChange={set("name")} placeholder="Sophie Martin" />
            <Input label="Email" type="email" value={form.email} onChange={set("email")} placeholder="sophie@email.fr" />
          </div>
          {error && <div style={{ fontSize: "0.7rem", color: "#E88A8A", marginTop: 16 }}>{error}</div>}
          <Btn onClick={pay} disabled={loading} style={{ width: "100%", padding: 15, marginTop: 28 }}>
            {loading ? "Redirection vers Stripe..." : `Payer ${p.price} € — continuer sur Stripe`}
          </Btn>
          <div style={{ fontSize: "0.9rem", color: "#6B6864", textAlign: "center", marginTop: 12, fontWeight: 400 }}>
            En procédant, vous acceptez nos <span style={{ color: S.gold }}>CGV</span>
          </div>
        </Card>
      </div>
    </div>
  );
};

//
//   PAGE : CONFIRMATION + FORMULAIRE CLIENT (apres paiement)
//
const ConfirmationPage = () => {
  const { sessionId } = useParams();
  const [state, setState] = useState("loading"); // loading | error | form | done
  const [form, setForm] = useState({ eventType: "wedding", names: "", eventDate: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    let active = true;
    const confirm = httpsCallable(functions, "confirmPayment");
    confirm({ sessionId })
      .then(({ data }) => {
        if (!active) return;
        setState(data.alreadySubmitted ? "done" : "form");
      })
      .catch(() => { if (active) setState("error"); });
    return () => { active = false; };
  }, [sessionId]);

  const submit = async () => {
    if (!form.names.trim() || !form.eventDate) {
      setSubmitError("Renseignez au moins les noms et la date de l'événement.");
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      await updateDoc(doc(db, "events", sessionId), {
        eventType: form.eventType,
        names: form.names,
        eventDate: form.eventDate,
        message: form.message,
        status: "infos_recues",
        submittedAt: serverTimestamp(),
      });
      setState("done");
    } catch (e) {
      setSubmitError("Échec de l'envoi, réessayez.");
      setSubmitting(false);
    }
  };

  const Centered = ({ children }) => (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: S.dark, padding: 24 }}>
      <div style={{ textAlign: "center", maxWidth: 440 }}>{children}</div>
    </div>
  );

  if (state === "loading") return (
    <Centered>
      <div style={{ fontSize: "1rem", letterSpacing: "0.15em", textTransform: "uppercase", color: "#6B6864" }}>Vérification du paiement...</div>
    </Centered>
  );

  if (state === "error") return (
    <Centered>
      <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "2rem", fontWeight: 300, color: S.cream, marginBottom: 12 }}>Paiement non confirmé</h2>
      <p style={{ fontSize: "1rem", color: "#4A4845", lineHeight: 1.9, fontWeight: 400 }}>
        Nous n'avons pas pu confirmer votre paiement. Si vous pensez qu'il s'agit d'une erreur, contactez-nous à contact@memoriesbyines.com.
      </p>
    </Centered>
  );

  if (state === "done") return (
    <Centered>
      <div style={{ fontSize: "4rem", marginBottom: 24 }}> </div>
      <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "2.2rem", fontWeight: 300, color: S.cream, marginBottom: 12 }}>Merci <em style={{ fontStyle: "italic", color: S.gold }}>!</em></h2>
      <p style={{ fontSize: "1rem", color: "#4A4845", lineHeight: 1.9, fontWeight: 400 }}>
        Votre demande a bien été enregistrée. Nous préparons votre page personnalisée et votre QR code, vous recevrez tout par email très prochainement.
      </p>
    </Centered>
  );

  return (
    <div style={{ minHeight: "100vh", background: S.dark, padding: "60px 24px", display: "flex", alignItems: "flex-start", justifyContent: "center" }}>
      <div style={{ width: "100%", maxWidth: 560 }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ fontSize: "3rem", marginBottom: 16 }}> </div>
          <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "2rem", fontWeight: 300, color: S.cream, marginBottom: 12 }}>Paiement <em style={{ fontStyle: "italic", color: S.gold }}>confirmé</em></h2>
          <p style={{ fontSize: "1rem", color: "#4A4845", lineHeight: 1.9, fontWeight: 400 }}>Encore un détail avant de créer votre page : parlez-nous de votre événement.</p>
        </div>
        <Card style={{ padding: 36 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Select label="Type d'événement" value={form.eventType} onChange={e => setForm(f => ({ ...f, eventType: e.target.value }))}>
              <option value="wedding">Mariage</option>
              <option value="birthday">Anniversaire</option>
              <option value="baptism">Baptême</option>
              <option value="corporate">Événement d'entreprise</option>
              <option value="anniversary">Autre</option>
            </Select>
            <Input label="Nom(s) des personnes concernées" value={form.names} onChange={e => setForm(f => ({ ...f, names: e.target.value }))} placeholder="Sophie & Thomas" />
            <Input label="Date de l'événement" type="date" value={form.eventDate} onChange={e => setForm(f => ({ ...f, eventDate: e.target.value }))} />
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ fontSize: "0.55rem", letterSpacing: "0.25em", textTransform: "uppercase", color: S.textLight }}>Message personnalisé (optionnel)</label>
              <textarea
                value={form.message}
                onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                placeholder="Un mot d'accueil pour vos invités..."
                style={{ background: S.dark3, border: "1px solid rgba(200,184,154,0.15)", padding: "11px 16px", color: S.cream, fontFamily: "'Montserrat',sans-serif", fontSize: "0.78rem", fontWeight: 300, minHeight: 80, resize: "vertical", width: "100%" }}
              />
            </div>
          </div>
          {submitError && <div style={{ fontSize: "0.7rem", color: "#E88A8A", marginTop: 16 }}>{submitError}</div>}
          <Btn onClick={submit} disabled={submitting} style={{ width: "100%", padding: 15, marginTop: 28 }}>
            {submitting ? "Envoi..." : "Envoyer ma demande"}
          </Btn>
        </Card>
      </div>
    </div>
  );
};

//
//   PAGE : DASHBOARD ADMIN
//
const DashboardPage = ({ onLogout, onPreviewEvent }) => {
  const [page, setPage] = useState("dashboard");
  const [events, setEvents] = useState([]);
  const [allMedia, setAllMedia] = useState([]);
  const [allAudio, setAllAudio] = useState([]);
  const [toast, setToast] = useState({ msg: "", visible: false });
  const [newEventModal, setNewEventModal] = useState(false);
  const [qrModal, setQrModal] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [filterType, setFilterType] = useState("all");
  const [search, setSearch] = useState("");
  const [newEvent, setNewEvent] = useState({ name: "", type: "wedding", date: "", location: "", welcomeMsg: "", color: S.gold, plan: "signature", pelliculeJetable: false });
  // Fichier separe de newEvent : un File n'est pas serialisable par Firestore
  // (newEvent est spreade directement dans addDoc dans createEvent).
  const [coverFile, setCoverFile] = useState(null);
  const [zipLoading, setZipLoading] = useState(false);
  const [eventZipLoadingId, setEventZipLoadingId] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const coverInputRef = useRef(null);
  const [visitStats, setVisitStats] = useState({ total: null, today: null });
  const [mediaFolder, setMediaFolder] = useState(null);

  const pickCoverFile = (file) => {
    if (coverPreview) URL.revokeObjectURL(coverPreview);
    setCoverFile(file);
    setCoverPreview(file ? URL.createObjectURL(file) : null);
  };

  useEffect(() => {
    const unsubEvents = onSnapshot(collection(db, "events"), snap => {
      setEvents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    const unsubMedia = onSnapshot(
      query(collectionGroup(db, "media"), orderBy("createdAt", "desc")),
      snap => setAllMedia(snap.docs.map(d => ({ id: d.id, eventId: d.ref.parent.parent.id, ...d.data(), time: timeAgo(d.data().createdAt) })))
    );
    const unsubAudio = onSnapshot(
      query(collectionGroup(db, "audio"), orderBy("createdAt", "desc")),
      snap => setAllAudio(snap.docs.map(d => ({ id: d.id, eventId: d.ref.parent.parent.id, ...d.data(), time: timeAgo(d.data().createdAt) })))
    );
    return () => { unsubEvents(); unsubMedia(); unsubAudio(); };
  }, []);

  useEffect(() => {
    if (page !== "analytics") return;
    (async () => {
      try {
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        const [totalSnap, todaySnap] = await Promise.all([
          getCountFromServer(collection(db, "visits")),
          getCountFromServer(query(collection(db, "visits"), where("createdAt", ">=", startOfToday))),
        ]);
        setVisitStats({ total: totalSnap.data().count, today: todaySnap.data().count });
      } catch (e) {
        setVisitStats({ total: 0, today: 0 });
      }
    })();
  }, [page]);

  const notify = msg => { setToast({ msg, visible: true }); setTimeout(() => setToast(t => ({ ...t, visible: false })), 3000); };

  // Les demandes issues du paiement Stripe (ConfirmationPage) vivent dans la
  // meme collection "events" mais n'ont pas encore de nom/slug/type definis
  // par l'admin : on les separe de la liste des evenements "prets".
  const pendingRequests = events.filter(e => PIPELINE_STATUSES.includes(e.status));
  const readyEvents = events.filter(e => !PIPELINE_STATUSES.includes(e.status));

  const markRequestProcessed = async (id) => {
    try {
      await updateDoc(doc(db, "events", id), { status: "traite" });
    } catch (e) {
      notify("Échec de la mise à jour");
    }
  };

  const filteredEvents = readyEvents.filter(e =>
    (filterType === "all" || e.type === filterType) &&
    (e.name.toLowerCase().includes(search.toLowerCase()) || e.location?.toLowerCase().includes(search.toLowerCase()))
  );

  const provisionAndShowQr = async (createdEvent) => {
    setQrModal({ ...createdEvent, codesLoading: true, guestCode: null, ownerCode: null });
    try {
      const provision = httpsCallable(functions, "provisionEventCodes");
      const { data } = await provision({ eventId: createdEvent.id });
      setQrModal(prev => prev && prev.id === createdEvent.id ? { ...prev, guestCode: data.guestCode, ownerCode: data.ownerCode, codesLoading: false } : prev);
    } catch (err) {
      notify("Échec de la génération des codes d'accès");
      setQrModal(prev => prev && prev.id === createdEvent.id ? { ...prev, codesLoading: false, codesError: true } : prev);
    }
  };

  const openQrModal = async (e) => {
    setQrModal({ ...e, codesLoading: true, guestCode: null, ownerCode: null });
    try {
      const getCodes = httpsCallable(functions, "getEventCodes");
      const { data } = await getCodes({ eventId: e.id });
      setQrModal(prev => prev && prev.id === e.id ? { ...prev, guestCode: data.guestCode, ownerCode: data.ownerCode, codesLoading: false } : prev);
    } catch (err) {
      notify("Échec de la récupération des codes d'accès");
      setQrModal(prev => prev && prev.id === e.id ? { ...prev, codesLoading: false, codesError: true } : prev);
    }
  };

  const createEvent = async () => {
    if (!newEvent.name.trim()) { notify("Renseignez le nom de l'événement"); return; }
    const slug = slugify(newEvent.name);
    try {
      const docRef = await addDoc(collection(db, "events"), {
        ...newEvent, status: "upcoming", photos: 0, audios: 0, videos: 0, guests: 0, slug,
        createdAt: serverTimestamp(),
      });
      setNewEventModal(false);
      notify(`"${newEvent.name}" créé`);
      const createdEvent = { id: docRef.id, ...newEvent, slug };
      setTimeout(() => provisionAndShowQr(createdEvent), 400);
      if (coverFile) {
        const coverToUpload = coverFile;
        (async () => {
          try {
            const coverRef = ref(storage, `events/${docRef.id}/cover.jpg`);
            await uploadBytes(coverRef, coverToUpload, { contentType: coverToUpload.type });
            const coverUrl = await getDownloadURL(coverRef);
            await updateDoc(docRef, { coverPhoto: coverUrl });
          } catch (e) {
            notify("Échec de l'envoi de la photo de fond");
          }
        })();
      }
      setNewEvent({ name: "", type: "wedding", date: "", location: "", welcomeMsg: "", color: S.gold, plan: "signature", pelliculeJetable: false });
      pickCoverFile(null);
    } catch (e) {
      notify("Échec de la création de l'événement");
    }
  };

  const deleteEvent = async (id) => {
    try {
      await deleteDoc(doc(db, "events", id));
      notify("Événement supprimé");
    } catch (e) {
      notify("Échec de la suppression");
    }
  };

  const togglePelliculeJetable = async (event, value) => {
    try {
      await updateDoc(doc(db, "events", event.id), { pelliculeJetable: value });
      notify(value ? "Mode pellicule jetable activé" : "Mode pellicule jetable désactivé");
    } catch (e) {
      notify("Échec de la mise à jour");
    }
  };

  const handleDashboardZip = async () => {
    const withUrl = allAudio.filter(a => a.url);
    if (!withUrl.length) { notify("Aucun audio à télécharger"); return; }
    setZipLoading(true);
    try {
      const zip = new JSZip();
      const eventNames = Object.fromEntries(events.map(e => [e.id, e.name || e.id]));
      const byEvent = {};
      withUrl.forEach(a => { (byEvent[a.eventId] = byEvent[a.eventId] || []).push(a); });
      await Promise.all(Object.entries(byEvent).map(async ([eventId, items]) => {
        const folder = zip.folder(eventNames[eventId] || eventId);
        await Promise.all(items.map(async (a, i) => {
          const res = await fetch(a.url);
          const blob = await res.blob();
          const safe = (a.author || "Anonyme").replace(/[^a-zA-Z0-9_À-ɏ\s-]/g, "_").trim();
          folder.file(`${String(i + 1).padStart(2, "0")}_${safe}.webm`, blob);
        }));
      }));
      const blob = await zip.generateAsync({ type: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "memories-by-ines_tous-les-audios.zip";
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error("[handleDashboardZip] échec :", err);
      notify("Échec du téléchargement");
    } finally {
      setZipLoading(false);
    }
  };

  const handleEventZip = async (e) => {
    setEventZipLoadingId(e.id);
    try {
      const media = allMedia.filter(m => m.eventId === e.id);
      const audio = allAudio.filter(a => a.eventId === e.id);
      const ok = await downloadEventZip(e, media, audio, notify);
      if (ok) notify(`Téléchargement de "${e.name}" prêt`);
    } catch (err) {
      console.error("[handleEventZip] échec :", err);
      notify("Échec du téléchargement");
    } finally {
      setEventZipLoadingId(null);
    }
  };

  const navItems = [
    ["dashboard", " ", "Tableau de bord"],
    ["requests", " ", "Nouvelles demandes"],
    ["events", " ", "Événements"],
    ["media", " ", "Médias"],
    ["audio", " ", "Messages"],
    ["clients", " ", "Clients"],
    ["analytics", " ", "Statistiques"],
    ["settings", " ", "Paramètres"],
  ];

  const Sidebar = () => (
    <aside style={{ width: 250, flexShrink: 0, background: S.dark2, borderRight: "1px solid rgba(200,184,154,0.08)", display: "flex", flexDirection: "column", position: "fixed", top: 0, left: 0, bottom: 0, zIndex: 100, transform: sidebarOpen ? "translateX(0)" : "translateX(-100%)", transition: "transform 0.3s" }}>
      <div style={{ padding: "28px 24px", borderBottom: "1px solid rgba(200,184,154,0.08)" }}>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.2rem", fontWeight: 400, color: S.cream }}>Memories <em style={{ fontStyle: "italic", color: S.gold }}>by Inès</em></div>
        <div style={{ fontSize: "0.5rem", letterSpacing: "0.25em", textTransform: "uppercase", color: S.textLight, marginTop: 4 }}>Dashboard admin</div>
      </div>
      <div style={{ flex: 1, padding: "20px 12px", overflowY: "auto" }}>
        <div style={{ fontSize: "0.48rem", letterSpacing: "0.3em", textTransform: "uppercase", color: S.textLight, padding: "0 12px", marginBottom: 8 }}>Navigation</div>
        {navItems.map(([id, icon, label]) => (
          <button key={id} onClick={() => setPage(id)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", width: "100%", background: page === id ? "rgba(200,184,154,0.08)" : "none", border: "none", color: page === id ? S.goldL : S.textLight, fontSize: "0.72rem", fontWeight: 300, cursor: "pointer", letterSpacing: "0.02em", transition: "all 0.2s", borderLeft: page === id ? `2px solid ${S.gold}` : "2px solid transparent" }}>
            <span style={{ fontSize: "0.85rem", width: 18, textAlign: "center" }}>{icon}</span> {label}
            {id === "events" && <span style={{ marginLeft: "auto", fontSize: "0.55rem", background: S.gold, color: S.dark, padding: "1px 6px", fontWeight: 500 }}>{readyEvents.length}</span>}
            {id === "requests" && pendingRequests.filter(r => r.status !== "traite").length > 0 && (
              <span style={{ marginLeft: "auto", fontSize: "0.55rem", background: "#9A3A3A", color: S.cream, padding: "1px 6px", fontWeight: 500 }}>
                {pendingRequests.filter(r => r.status !== "traite").length}
              </span>
            )}
          </button>
        ))}
      </div>
      <div style={{ padding: "16px 12px", borderTop: "1px solid rgba(200,184,154,0.08)", display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ width: 34, height: 34, borderRadius: "50%", background: `linear-gradient(135deg,${S.gold},${S.goldL})`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Cormorant Garamond',serif", fontSize: "1rem", fontStyle: "italic", color: S.dark, flexShrink: 0 }}>I</div>
        <div>
          <div style={{ fontSize: "0.73rem", color: S.cream, fontWeight: 300 }}>Inès</div>
          <div style={{ fontSize: "0.55rem", color: S.textLight, letterSpacing: "0.1em" }}>Administratrice</div>
        </div>
        <button onClick={onLogout} style={{ marginLeft: "auto", background: "none", border: "none", color: S.textLight, fontSize: "0.7rem", cursor: "pointer" }} title="Déconnexion"> </button>
      </div>
    </aside>
  );

  const StatCard = ({ icon, value, label, delta, highlight }) => (
    <Card style={{ padding: "24px 28px", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: highlight ? `linear-gradient(90deg,transparent,${S.gold},transparent)` : "rgba(200,184,154,0.15)" }} />
      <div style={{ fontSize: "1.1rem", marginBottom: 14, opacity: 0.7 }}>{icon}</div>
      <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "2.6rem", fontWeight: 300, color: S.goldL, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: "0.58rem", letterSpacing: "0.2em", textTransform: "uppercase", color: S.textLight, marginTop: 6 }}>{label}</div>
      {delta && <div style={{ fontSize: "0.6rem", color: S.green, marginTop: 6 }}>  {delta}</div>}
    </Card>
  );

  const EventCard = ({ e }) => (
    <Card style={{ borderLeft: `2px solid transparent`, transition: "all 0.3s", cursor: "pointer" }}
      onMouseEnter={el => el.currentTarget.style.borderLeftColor = e.color || S.gold}
      onMouseLeave={el => el.currentTarget.style.borderLeftColor = "transparent"}>
      <div style={{ padding: "18px 20px 0", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Badge type={e.type} />
        <StatusDot status={e.status} />
      </div>
      <div style={{ padding: "12px 20px 20px" }}>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.2rem", color: S.cream, marginBottom: 4 }}>{e.name}</div>
        <div style={{ fontSize: "0.62rem", color: S.textLight, marginBottom: 16 }}>{e.date} {e.location ? `  ${e.location}` : ""}</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 1, marginBottom: 10, background: "rgba(200,184,154,0.06)" }}>
          {[["Photos", e.photos], ["Vidéos", e.videos], ["Messages", e.audios]].map(([l, v]) => (
            <div key={l} style={{ background: S.dark2, padding: "10px", textAlign: "center" }}>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.3rem", color: S.goldL }}>{v}</div>
              <div style={{ fontSize: "0.5rem", textTransform: "uppercase", letterSpacing: "0.1em", color: S.textLight, marginTop: 2 }}>{l}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: "0.58rem", color: S.textLight, marginBottom: 16 }}>
          {(e.photos || 0) + (e.videos || 0) + (e.audios || 0)} fichier{((e.photos || 0) + (e.videos || 0) + (e.audios || 0)) > 1 ? "s" : ""} au total
        </div>
        <div style={{ marginBottom: 16 }}>
          <PelliculeToggleField
            plan={e.plan}
            checked={!!e.pelliculeJetable}
            onChange={v => togglePelliculeJetable(e, v)}
          />
        </div>
        <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
          <button onClick={() => onPreviewEvent(e)} style={{ flex: 1, padding: "8px", fontSize: "0.55rem", letterSpacing: "0.15em", textTransform: "uppercase", background: "rgba(200,184,154,0.1)", color: S.gold, border: `1px solid rgba(200,184,154,0.2)`, cursor: "pointer" }}>Voir la page</button>
          <button onClick={() => openQrModal(e)} style={{ flex: 1, padding: "8px", fontSize: "0.55rem", letterSpacing: "0.15em", textTransform: "uppercase", background: S.dark3, color: S.textLight, border: "1px solid rgba(200,184,154,0.3)", cursor: "pointer" }}>QR code</button>
          <button onClick={() => deleteEvent(e.id)} style={{ padding: "8px 12px", background: "rgba(154,90,90,0.1)", color: "#9A5A5A", border: "1px solid rgba(154,90,90,0.2)", cursor: "pointer", fontSize: "0.8rem" }}> </button>
        </div>
        <button
          onClick={() => handleEventZip(e)}
          disabled={eventZipLoadingId === e.id}
          style={{ width: "100%", padding: "8px", fontSize: "0.55rem", letterSpacing: "0.15em", textTransform: "uppercase", background: S.gold, color: S.dark, border: "none", cursor: eventZipLoadingId === e.id ? "default" : "pointer", opacity: eventZipLoadingId === e.id ? 0.6 : 1 }}
        >
          {eventZipLoadingId === e.id ? "Préparation du ZIP..." : "Télécharger tout"}
        </button>
      </div>
    </Card>
  );

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      <style>{css}
        {`@keyframes blink{0%,100%{opacity:1}50%{opacity:0.3}}
          @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
          @keyframes recordPulse{0%,100%{box-shadow:0 0 0 0 rgba(200,184,154,0.4)}50%{box-shadow:0 0 0 18px rgba(200,184,154,0)}}
          @keyframes waveAnim{from{height:4px}to{height:28px}}`}
      </style>
      <Toast msg={toast.msg} visible={toast.visible} />
      <Sidebar />

      {/* Overlay mobile */}
      {sidebarOpen && window.innerWidth <= 768 && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 99 }} onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main */}
      <div style={{ flex: 1, marginLeft: sidebarOpen && window.innerWidth > 768 ? 250 : 0, display: "flex", flexDirection: "column", transition: "margin 0.3s" }}>
        {/* Topbar */}
        <div style={{ padding: "18px 32px", borderBottom: "1px solid rgba(200,184,154,0.08)", display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(250,248,245,0.97)", backdropFilter: "blur(10px)", position: "sticky", top: 0, zIndex: 50, flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={() => setSidebarOpen(o => !o)} style={{ background: "none", border: "none", color: S.textLight, fontSize: "1.2rem", cursor: "pointer" }}> </button>
            <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.3rem", fontWeight: 300, fontStyle: "italic", color: S.cream }}>
              {{ dashboard: "Tableau de bord", requests: "Nouvelles demandes", events: "Mes événements", media: "Galerie médias", audio: "Livre audio", clients: "Clients", analytics: "Statistiques", settings: "Paramètres" }[page]}
            </span>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: S.dark3, border: "1px solid rgba(200,184,154,0.12)", padding: "8px 14px" }}>
              <span style={{ color: S.textLight, fontSize: "0.75rem" }}> </span>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..." style={{ background: "none", border: "none", color: S.cream, fontFamily: "'Montserrat',sans-serif", fontSize: "0.72rem", width: 150 }} />
            </div>
            <Btn onClick={() => setNewEventModal(true)} style={{ padding: "10px 20px" }}>+ Nouvel événement</Btn>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: 32, flex: 1, animation: "fadeIn 0.4s ease" }}>

          {/* DASHBOARD */}
          {page === "dashboard" && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 2, marginBottom: 36 }}>
                <StatCard icon=" " value={readyEvents.length} label="Événements" delta="2 ce mois" highlight />
                <StatCard icon=" " value={readyEvents.reduce((s, e) => s + e.photos + e.videos, 0)} label="Médias partagés" delta="51 cette semaine" />
                <StatCard icon=" " value={readyEvents.reduce((s, e) => s + e.audios, 0)} label="Messages vocaux" delta="12 cette semaine" />
                <StatCard icon=" " value="847 €" label="Revenus" delta="199 € ce mois" />
              </div>
              <div style={{ marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.4rem", fontWeight: 300 }}>Événements <em style={{ fontStyle: "italic", color: S.goldL }}>récents</em></div>
                <button onClick={() => setPage("events")} style={{ background: "none", border: `1px solid rgba(200,184,154,0.2)`, color: S.textLight, padding: "7px 16px", fontSize: "0.6rem", letterSpacing: "0.2em", cursor: "pointer" }}>Voir tout</button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 2 }}>
                {readyEvents.slice(0, 4).map(e => <EventCard key={e.id} e={e} />)}
              </div>
            </>
          )}

          {/* REQUESTS */}
          {page === "requests" && (
            <Card>
              {pendingRequests.length === 0 ? (
                <div style={{ padding: 40, textAlign: "center", color: S.textLight, fontSize: "0.75rem" }}>
                  Aucune nouvelle demande pour le moment.
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr style={{ borderBottom: "1px solid rgba(200,184,154,0.1)" }}>
                    {["Statut", "Formule", "Montant", "Email", "Type", "Noms", "Date événement", "Message", "Action"].map(h => (
                      <th key={h} style={{ padding: "12px 16px", fontSize: "0.5rem", letterSpacing: "0.2em", textTransform: "uppercase", color: S.textLight, textAlign: "left", fontWeight: 400 }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {pendingRequests.map(r => {
                      const [statusText, statusColor] = { paiement_confirme: ["En attente d'infos", S.gold], infos_recues: ["À traiter", "#E88A8A"], traite: ["Traité", S.textLight] }[r.status] || [r.status, S.textLight];
                      return (
                        <tr key={r.id} style={{ borderBottom: "1px solid rgba(200,184,154,0.15)" }}>
                          <td style={{ padding: "14px 16px" }}><span style={{ fontSize: "0.6rem", color: statusColor }}>{statusText}</span></td>
                          <td style={{ padding: "14px 16px", fontSize: "0.7rem", color: S.gold }}>{r.plan || "—"}</td>
                          <td style={{ padding: "14px 16px", fontSize: "0.7rem" }}>{r.amount ? `${(r.amount / 100).toFixed(0)} €` : "—"}</td>
                          <td style={{ padding: "14px 16px", fontSize: "0.7rem", color: S.textLight }}>{r.email || "—"}</td>
                          <td style={{ padding: "14px 16px", fontSize: "0.7rem" }}>{r.eventType || "—"}</td>
                          <td style={{ padding: "14px 16px", fontSize: "0.7rem" }}>{r.names || "—"}</td>
                          <td style={{ padding: "14px 16px", fontSize: "0.7rem" }}>{r.eventDate || "—"}</td>
                          <td style={{ padding: "14px 16px", fontSize: "0.65rem", color: S.textLight, maxWidth: 200 }}>{r.message || "—"}</td>
                          <td style={{ padding: "14px 16px" }}>
                            {r.status !== "traite" && (
                              <button onClick={() => markRequestProcessed(r.id)} style={{ background: "rgba(200,184,154,0.1)", border: `1px solid rgba(200,184,154,0.2)`, color: S.gold, padding: "5px 14px", fontSize: "0.55rem", letterSpacing: "0.15em", cursor: "pointer" }}>Marquer traité</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </Card>
          )}

          {/* EVENTS */}
          {page === "events" && (
            <>
              <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
                <Select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ width: "auto" }}>
                  <option value="all">Tous les types</option>
                  <option value="wedding">Mariages</option>
                  <option value="birthday">Anniversaires</option>
                  <option value="corporate">Corporate</option>
                  <option value="baptism">Baptêmes</option>
                </Select>
                <div style={{ fontSize: "0.65rem", color: S.textLight, alignSelf: "center" }}>{filteredEvents.length} événement{filteredEvents.length > 1 ? "s" : ""}</div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 2 }}>
                {filteredEvents.map(e => <EventCard key={e.id} e={e} />)}
              </div>
            </>
          )}

          {/* MEDIA */}
          {page === "media" && (() => {
            const eventsWithMedia = readyEvents.filter(e => allMedia.some(m => m.eventId === e.id));
            const openEvent = mediaFolder ? events.find(ev => ev.id === mediaFolder) : null;

            if (!mediaFolder || !openEvent) {
              return (
                <>
                  <div style={{ fontSize: "0.62rem", color: S.textLight, marginBottom: 20 }}>{allMedia.length} fichiers — {eventsWithMedia.length} dossier{eventsWithMedia.length > 1 ? "s" : ""}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 3 }}>
                    {eventsWithMedia.map(e => {
                      const count = allMedia.filter(m => m.eventId === e.id).length;
                      return (
                        <Card key={e.id} onClick={() => setMediaFolder(e.id)}
                          style={{ padding: "26px 20px", textAlign: "center", cursor: "pointer", transition: "border-color 0.2s" }}
                          onMouseEnter={el => el.currentTarget.style.borderColor = S.gold}
                          onMouseLeave={el => el.currentTarget.style.borderColor = "rgba(200,184,154,0.2)"}
                        >
                          <div style={{ fontSize: "2rem", marginBottom: 10, color: S.gold }}>🗂</div>
                          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.05rem", color: S.cream, marginBottom: 4 }}>{e.name}</div>
                          <div style={{ fontSize: "0.58rem", color: S.textLight, letterSpacing: "0.05em" }}>{count} fichier{count > 1 ? "s" : ""}</div>
                        </Card>
                      );
                    })}
                  </div>
                  {allMedia.length === 0 && (
                    <div style={{ padding: 40, textAlign: "center", color: S.textLight, fontSize: "0.75rem" }}>Aucun média pour le moment.</div>
                  )}
                </>
              );
            }

            const eventMedia = allMedia.filter(m => m.eventId === mediaFolder);
            return (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
                  <button onClick={() => setMediaFolder(null)} style={{ background: "none", border: "none", color: S.gold, fontSize: "0.65rem", letterSpacing: "0.1em", textTransform: "uppercase", cursor: "pointer", padding: 0 }}>← Tous les dossiers</button>
                  <Btn onClick={() => handleEventZip(openEvent)} disabled={eventZipLoadingId === openEvent.id} style={{ padding: "8px 18px" }}>
                    {eventZipLoadingId === openEvent.id ? "Préparation..." : "Télécharger"}
                  </Btn>
                </div>
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.3rem", color: S.cream, marginBottom: 20 }}>
                  {openEvent.name} <span style={{ fontSize: "0.6rem", color: S.textLight, fontFamily: "'Montserrat',sans-serif" }}>({eventMedia.length} fichier{eventMedia.length > 1 ? "s" : ""})</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(130px,1fr))", gap: 3 }}>
                  {eventMedia.map(m => (
                    <div key={m.id} onClick={() => window.open(m.url, "_blank")} style={{ aspectRatio: "1", background: S.dark3, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2rem", cursor: "pointer", position: "relative", overflow: "hidden" }}>
                      {m.url && (m.type === "video"
                        ? <video src={m.url} style={{ width: "100%", height: "100%", objectFit: "cover" }} muted />
                        : <img src={m.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />)}
                      {m.type === "video" && <div style={{ position: "absolute", top: 6, right: 6, background: "rgba(0,0,0,0.6)", color: S.cream, fontSize: "0.55rem", padding: "2px 5px" }}>Vidéo</div>}
                      <div style={{ position: "absolute", bottom: 4, left: 4, fontSize: "0.55rem", color: "#FFFFFF", background: "rgba(44,35,24,0.45)", padding: "1px 4px" }}>{m.author}</div>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}

          {/* AUDIO */}
          {page === "audio" && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
                <div style={{ fontSize: "0.62rem", color: S.textLight }}>{allAudio.length} messages vocaux</div>
                <Btn onClick={handleDashboardZip} disabled={zipLoading} style={{ padding: "8px 20px" }}>
                  {zipLoading ? "Préparation..." : "Télécharger tous les audios (ZIP)"}
                </Btn>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {allAudio.map(a => <AudioRow key={a.id} audio={a} accent={S.gold} />)}
              </div>
            </>
          )}

          {/* CLIENTS */}
          {page === "clients" && (
            <Card>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr style={{ borderBottom: "1px solid rgba(200,184,154,0.1)" }}>
                  {["Client", "Email", "Formule", "Statut", "Actions"].map(h => <th key={h} style={{ padding: "12px 20px", fontSize: "0.52rem", letterSpacing: "0.25em", textTransform: "uppercase", color: S.textLight, textAlign: "left", fontWeight: 400 }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {[["Sophie & Thomas", "sophie.thomas@gmail.com", "Prestige", "live"],
                    ["Famille Dubois", "m.dubois@orange.fr", "Essentiel", "done"],
                    ["TechCorp SAS", "events@techcorp.fr", "Signature", "upcoming"],
                  ].map(([n, e, p, s]) => (
                    <tr key={n} style={{ borderBottom: "1px solid rgba(200,184,154,0.15)" }}>
                      <td style={{ padding: "14px 20px", fontSize: "0.73rem" }}>{n}</td>
                      <td style={{ padding: "14px 20px", fontSize: "0.7rem", color: S.textLight }}>{e}</td>
                      <td style={{ padding: "14px 20px" }}><span style={{ fontSize: "0.62rem", color: S.gold }}>{p}</span></td>
                      <td style={{ padding: "14px 20px" }}><StatusDot status={s} /></td>
                      <td style={{ padding: "14px 20px" }}><button onClick={() => notify("Fiche client ouverte  ")} style={{ background: "rgba(200,184,154,0.1)", border: `1px solid rgba(200,184,154,0.2)`, color: S.gold, padding: "5px 14px", fontSize: "0.55rem", letterSpacing: "0.15em", cursor: "pointer" }}>Voir</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {/* ANALYTICS */}
          {page === "analytics" && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 2 }}>
              <StatCard icon=" " value={visitStats.total ?? "…"} label="Visites totales" highlight />
              <StatCard icon=" " value={visitStats.today ?? "…"} label="Visites aujourd'hui" />
            </div>
          )}

          {/* SETTINGS */}
          {page === "settings" && (
            <div style={{ maxWidth: 500, display: "flex", flexDirection: "column", gap: 16 }}>
              <Input label="Nom affiché" defaultValue="Inès" />
              <Input label="Email" type="email" defaultValue="contact@memoriesbyines.com" />
              <Input label="Domaine personnalisé" defaultValue="memoriesbyines.com" />
              <div style={{ marginTop: 8 }}>
                <Btn onClick={() => notify("Paramètres sauvegardés  ")}>Sauvegarder</Btn>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* MODAL: Nouvel evenement */}
      <Modal open={newEventModal} onClose={() => setNewEventModal(false)} title="Nouvel événement" subtitle="Remplissez les détails pour générer le QR code" maxWidth={580}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Input label="Nom de l'événement *" value={newEvent.name} onChange={e => setNewEvent(n => ({ ...n, name: e.target.value }))} placeholder="Mariage Sophie & Thomas" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Select label="Type" value={newEvent.type} onChange={e => setNewEvent(n => ({ ...n, type: e.target.value }))}>
              <option value="wedding">  Mariage</option>
              <option value="birthday">  Anniversaire</option>
              <option value="corporate">  Corporate</option>
              <option value="baptism">  Baptême</option>
              <option value="anniversary">  Autre</option>
            </Select>
            <Input label="Date" type="date" value={newEvent.date} onChange={e => setNewEvent(n => ({ ...n, date: e.target.value }))} />
          </div>
          <Input label="Lieu" value={newEvent.location} onChange={e => setNewEvent(n => ({ ...n, location: e.target.value }))} placeholder="Château de Fontainebleau..." />
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: "0.55rem", letterSpacing: "0.25em", textTransform: "uppercase", color: S.textLight }}>Photo de fond (optionnel)</label>
            <input
              ref={coverInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={e => pickCoverFile(e.target.files?.[0] || null)}
            />
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {coverPreview && (
                <img src={coverPreview} alt="" style={{ width: 56, height: 56, objectFit: "cover", border: "1px solid rgba(200,184,154,0.2)" }} />
              )}
              <Btn variant="ghost" onClick={() => coverInputRef.current.click()} style={{ padding: "10px 18px" }}>
                {coverPreview ? "Changer la photo" : "Choisir une photo"}
              </Btn>
              {coverPreview && (
                <button onClick={() => pickCoverFile(null)} style={{ background: "none", border: "none", color: S.textLight, fontSize: "0.6rem", letterSpacing: "0.15em", cursor: "pointer", textDecoration: "underline" }}>Retirer</button>
              )}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: "0.55rem", letterSpacing: "0.25em", textTransform: "uppercase", color: S.textLight }}>Message de bienvenue</label>
            <textarea value={newEvent.welcomeMsg} onChange={e => setNewEvent(n => ({ ...n, welcomeMsg: e.target.value }))} placeholder="Un mot d'accueil pour vos invités..." style={{ background: S.dark3, border: "1px solid rgba(200,184,154,0.15)", padding: "11px 16px", color: S.cream, fontFamily: "'Montserrat',sans-serif", fontSize: "0.78rem", fontWeight: 300, minHeight: 80, resize: "vertical", width: "100%" }} />
          </div>
          <div>
            <label style={{ fontSize: "0.55rem", letterSpacing: "0.25em", textTransform: "uppercase", color: S.textLight, display: "block", marginBottom: 8 }}>Couleur accent</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {["#C9A96E", "#E8C4B0", "#C4D4E8", "#C4E8C8", "#D4C4E8", "#E8E0C4", "#E8C4C4"].map(c => (
                <div key={c} onClick={() => setNewEvent(n => ({ ...n, color: c }))} style={{ width: 30, height: 30, borderRadius: "50%", background: c, cursor: "pointer", border: `2px solid ${newEvent.color === c ? "#fff" : "transparent"}`, transition: "border 0.2s" }} />
              ))}
            </div>
          </div>
          <Select label="Formule" value={newEvent.plan} onChange={e => {
            const plan = e.target.value;
            setNewEvent(n => ({ ...n, plan, pelliculeJetable: plan === "essentiel" ? false : n.pelliculeJetable }));
          }}>
            <option value="essentiel">Essentiel   69 €</option>
            <option value="prestige">Prestige   89 €</option>
            <option value="signature">Signature   129 €</option>
          </Select>
          <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 6, margin: "10px 0 0" }}>
            {PLAN_INFO[newEvent.plan].features.map(f => (
              <li key={f} style={{ fontSize: "0.68rem", color: S.textLight, fontWeight: 300, display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ color: S.gold }}> </span> {f}
              </li>
            ))}
          </ul>
          <PelliculeToggleField
            plan={newEvent.plan}
            checked={newEvent.pelliculeJetable}
            onChange={v => setNewEvent(n => ({ ...n, pelliculeJetable: v }))}
          />
          <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
            <Btn onClick={createEvent} style={{ flex: 1, padding: 13 }}>Créer & générer le QR  </Btn>
            <Btn variant="ghost" onClick={() => setNewEventModal(false)}>Annuler</Btn>
          </div>
        </div>
      </Modal>

      {/* MODAL: QR Code */}
      <Modal open={!!qrModal} onClose={() => setQrModal(null)} title={qrModal?.name || ""} subtitle={qrModal?.date || ""} maxWidth={560}>
        {qrModal && (
          <div style={{ textAlign: "center" }}>
            {qrModal.codesLoading ? (
              <div style={{ padding: "32px 0", color: S.textLight, fontSize: "0.75rem" }}>Génération des codes d'accès...</div>
            ) : qrModal.codesError ? (
              <div style={{ padding: "32px 0", color: "#E88A8A", fontSize: "0.75rem" }}>Impossible de récupérer les codes d'accès.</div>
            ) : (
              <div style={{ display: "flex", gap: 24, justifyContent: "center", flexWrap: "wrap" }}>
                {[
                  { label: "QR Invités", code: qrModal.guestCode },
                  { label: "QR Accès complet", code: qrModal.ownerCode },
                ].map(({ label, code }) => (
                  <div key={label} style={{ flex: "1 1 200px", maxWidth: 220 }}>
                    <div style={{ fontSize: "0.6rem", letterSpacing: "0.2em", textTransform: "uppercase", color: S.gold, marginBottom: 12 }}>{label}</div>
                    <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
                      <QRCode slug={qrModal.slug || qrModal.id} code={code} size={150} />
                    </div>
                    <div style={{ fontSize: "0.9rem", color: S.cream, letterSpacing: "0.15em", fontFamily: "monospace" }}>{code}</div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ fontSize: "0.62rem", color: S.gold, letterSpacing: "0.1em", margin: "24px 0", fontFamily: "monospace" }}>
              {SITE_DOMAIN}/event/{qrModal.slug || qrModal.id}
            </div>
            <div style={{ marginTop: 16 }}>
              <Btn onClick={() => { setQrModal(null); onPreviewEvent(qrModal); }} style={{ width: "100%", padding: 12 }} variant="ghost">
                Prévisualiser la page invité
              </Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

//
//   PAGE : DEMO (vue invitee statique, sans backend)
//
const DEMO_EVENT = {
  name: "Sophie & Thomas",
  date: "14 juin 2026",
  location: "Château de Fontainebleau",
  welcomeMsg: "Merci d'être avec nous pour ce jour si précieux. Partagez vos plus beaux souvenirs avec nous !",
  coverPhoto: "https://images.unsplash.com/photo-1606490194859-07c18c9f0968?w=1600&q=80&auto=format&fit=crop",
};

const DEMO_PHOTOS = [
  { id: 1, url: "https://images.unsplash.com/photo-1519741497674-611481863552?w=900&q=80&auto=format&fit=crop", author: "Clara" },
  { id: 2, url: "https://images.unsplash.com/photo-1606490194859-07c18c9f0968?w=900&q=80&auto=format&fit=crop", author: "Antoine" },
  { id: 3, url: "https://images.unsplash.com/photo-1591604466107-ec97de577aff?w=900&q=80&auto=format&fit=crop", author: "Juliette" },
  { id: 4, url: "https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=900&q=80&auto=format&fit=crop", author: "Famille Martin" },
  { id: 5, url: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=900&q=80&auto=format&fit=crop", author: "Les témoins" },
];

const DEMO_AUDIOS = [
  { id: 1, author: "Léa", duration: "1:24" },
  { id: 2, author: "Marc", duration: "0:47" },
];

const DemoAudioRow = ({ audio }) => (
  <div style={{ background: S.dark2, padding: "18px 20px", display: "flex", alignItems: "center", gap: 14, border: "1px solid rgba(200,184,154,0.08)" }}>
    <div
      title="Aperçu non disponible dans la démo"
      style={{ width: 40, height: 40, borderRadius: "50%", background: "rgba(200,184,154,0.06)", border: "1px solid rgba(200,184,154,0.25)", color: S.textLight, fontSize: "0.8rem", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", cursor: "not-allowed" }}
    >
      ▶
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1rem", color: S.cream }}>{audio.author}</div>
      <div style={{ height: 2, background: "rgba(200,184,154,0.12)", marginTop: 8 }} />
    </div>
    <div style={{ fontSize: "0.62rem", color: S.textLight, flexShrink: 0 }}>{audio.duration}</div>
  </div>
);

const DemoPage = () => {
  const [tab, setTab] = useState("photos");
  const [lightbox, setLightbox] = useState(null);
  const accent = S.gold;
  const bannerRef = useRef(null);
  const [bannerHeight, setBannerHeight] = useState(48);
  useEffect(() => {
    const measure = () => setBannerHeight(bannerRef.current ? bannerRef.current.offsetHeight : 48);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  return (
    <div style={{ minHeight: "100vh", background: S.dark }}>
      <style>{css}
        {`@keyframes blink{0%,100%{opacity:1}50%{opacity:0.3}}
          @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}`}
      </style>

      <div ref={bannerRef} style={{ position: "sticky", top: 0, zIndex: 200, background: `linear-gradient(90deg, ${S.goldL}, ${S.gold})`, color: S.dark, padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "center", gap: 16, flexWrap: "wrap", textAlign: "center" }}>
        <span style={{ fontSize: "0.7rem", fontWeight: 500, letterSpacing: "0.02em" }}>
          ✦ Page de démonstration — Créez la vôtre sur <strong>memoriesbyines.com</strong>
        </span>
        <button onClick={() => window.location.href = "/checkout"} style={{ background: S.dark, color: S.goldL, border: "none", padding: "8px 18px", fontSize: "0.6rem", letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 500, cursor: "pointer", flexShrink: 0 }}>
          Commencer
        </button>
      </div>

      {lightbox && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(11,9,6,0.97)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setLightbox(null)}>
          <button style={{ position: "absolute", top: 20, right: 20, background: "none", border: "none", color: S.textLight, fontSize: "1.5rem", cursor: "pointer" }}>×</button>
          <img src={lightbox.url} style={{ maxWidth: "90vw", maxHeight: "85vh", objectFit: "contain" }} alt="" />
        </div>
      )}

      {/* Hero */}
      <div style={{ minHeight: "90vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "80px 24px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${DEMO_EVENT.coverPhoto})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div style={{ position: "absolute", inset: 0, background: "rgba(250,248,245,0.6)" }} />
        <div style={{ position: "absolute", inset: 0, background: `radial-gradient(ellipse 60% 50% at 50% 40%, ${accent}18 0%, transparent 60%)` }} />
        {[500, 750, 1050].map((s, i) => (
          <div key={i} style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: s, height: s, borderRadius: "50%", border: `1px solid ${accent}12`, animation: `blink ${3 + i}s ease-in-out infinite`, animationDelay: `${i}s`, pointerEvents: "none" }} />
        ))}
        <a href="/" style={{ position: "absolute", top: bannerHeight + 20, left: 20, background: "none", border: `1px solid rgba(200,184,154,0.2)`, color: S.textLight, padding: "8px 16px", fontSize: "0.6rem", letterSpacing: "0.15em", textDecoration: "none", zIndex: 10 }}>← Retour</a>
        <div style={{ position: "relative", zIndex: 1 }}>
          <div style={{ fontSize: "0.58rem", letterSpacing: "0.4em", textTransform: "uppercase", color: accent, border: `1px solid ${accent}44`, padding: "7px 18px", marginBottom: 36, display: "inline-block" }}>✦ Partagez vos souvenirs ✦</div>
          <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(3rem,10vw,6.5rem)", fontWeight: 300, lineHeight: 1, color: S.cream, marginBottom: 16 }}>{DEMO_EVENT.name}</h1>
          <div style={{ fontSize: "0.68rem", letterSpacing: "0.3em", textTransform: "uppercase", color: S.textLight, marginBottom: 8 }}>{DEMO_EVENT.date}</div>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.1rem", fontStyle: "italic", color: S.textMid, marginBottom: 16 }}>{DEMO_EVENT.location}</div>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.4rem", fontWeight: 500, fontStyle: "italic", color: S.cream, maxWidth: 560, margin: "0 auto", lineHeight: 1.7 }}>"{DEMO_EVENT.welcomeMsg}"</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ position: "sticky", top: bannerHeight, zIndex: 50, background: "rgba(250,248,245,0.96)", backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(200,184,154,0.25)" }}>
        <div style={{ display: "flex", maxWidth: 600, margin: "0 auto" }}>
          {[["photos", "📷", "Photos & Vidéos"], ["audio", "🎙", "Messages vocaux"]].map(([id, icon, label]) => (
            <button key={id} onClick={() => setTab(id)} style={{ flex: 1, padding: "17px 12px", background: "none", border: "none", color: tab === id ? accent : "#6B6864", fontSize: "0.72rem", letterSpacing: "0.2em", textTransform: "uppercase", cursor: "pointer", borderBottom: tab === id ? `2px solid ${accent}` : "2px solid transparent", transition: "all 0.3s", display: "flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
              {icon} {label}
            </button>
          ))}
        </div>
      </div>

      {/* PANEL: Photos */}
      {tab === "photos" && (
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "60px 24px" }}>
          <div style={{ fontSize: "0.72rem", letterSpacing: "0.3em", textTransform: "uppercase", color: "#6B6864", marginBottom: 10, textAlign: "center" }}>Aperçu</div>
          <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(1.8rem,4vw,2.6rem)", fontWeight: 400, textAlign: "center", marginBottom: 16, color: "#2C2A25" }}>
            Photos & vidéos des invités
          </h2>
          <p style={{ fontSize: "1rem", color: "#4A4845", textAlign: "center", marginBottom: 40, fontWeight: 400, maxWidth: 480, marginLeft: "auto", marginRight: "auto" }}>
            Exemple de galerie. Sur votre page, vos invités déposent leurs propres photos et vidéos directement depuis leur téléphone.
          </p>
          <div style={{ columns: window.innerWidth < 600 ? 2 : 3, columnGap: 4 }}>
            {DEMO_PHOTOS.map(m => (
              <div key={m.id} onClick={() => setLightbox(m)} style={{ breakInside: "avoid", marginBottom: 4, position: "relative", overflow: "hidden", cursor: "pointer" }}>
                <img src={m.url} style={{ width: "100%", display: "block", objectFit: "cover" }} alt="" loading="lazy" />
                <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top,rgba(11,9,6,0.7) 0%,transparent 50%)", opacity: 0, transition: "opacity 0.3s" }}
                  onMouseEnter={e => e.currentTarget.style.opacity = 1}
                  onMouseLeave={e => e.currentTarget.style.opacity = 0}>
                  <div style={{ position: "absolute", bottom: 8, left: 8, fontSize: "0.6rem", color: S.cream }}>{m.author}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* PANEL: Audio */}
      {tab === "audio" && (
        <div style={{ maxWidth: 680, margin: "0 auto", padding: "60px 24px" }}>
          <div style={{ textAlign: "center", marginBottom: 16 }}>
            <div style={{ fontSize: "0.72rem", letterSpacing: "0.3em", textTransform: "uppercase", color: "#6B6864", marginBottom: 10 }}>Aperçu</div>
            <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(1.8rem,4vw,2.6rem)", fontWeight: 400, color: "#2C2A25" }}>Messages vocaux des invités</h2>
          </div>
          <p style={{ fontSize: "1rem", color: "#4A4845", textAlign: "center", marginBottom: 32, fontWeight: 400 }}>
            Exemple de messages reçus — l'écoute n'est pas disponible dans cette démonstration.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {DEMO_AUDIOS.map(a => <DemoAudioRow key={a.id} audio={a} />)}
          </div>
        </div>
      )}

      {/* Footer + CTA */}
      <div style={{ borderTop: "1px solid rgba(200,184,154,0.1)", padding: "60px 24px", textAlign: "center" }}>
        <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.8rem", fontWeight: 300, color: S.cream, marginBottom: 24 }}>
          Envie d&apos;une page comme celle-ci pour votre événement ?
        </h3>
        <Btn onClick={() => window.location.href = "/checkout"} style={{ marginBottom: 40 }}>Commencer</Btn>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "0.9rem", fontWeight: 300, color: S.textLight }}>
          Memories <span style={{ fontStyle: "italic", color: accent }}>by Inès</span>
        </div>
      </div>
    </div>
  );
};

//
//   LANDING PAGE
//
const LandingPage = ({ onDemo, onDashboard, onCheckout }) => {
  const [scrolled, setScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [navOpen, setNavOpen] = useState(false);
  const navRef = useRef(null);
  const [navHeight, setNavHeight] = useState(72);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("scroll", onScroll);
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onResize); };
  }, []);

  useEffect(() => {
    if (navRef.current) setNavHeight(navRef.current.offsetHeight);
  }, [isMobile, scrolled, navOpen]);

  useEffect(() => {
    // Ne compte pas les propres visites de l'admin, et une seule fois par
    // onglet (evite de gonfler le compteur au moindre re-render/navigation).
    if (auth.currentUser || sessionStorage.getItem("visit_logged")) return;
    sessionStorage.setItem("visit_logged", "1");
    addDoc(collection(db, "visits"), { createdAt: serverTimestamp() }).catch(() => {});
  }, []);

  const plans = Object.entries(PLAN_INFO).map(([id, info]) => ({ id, ...info }));

  const steps = [
    { n: "01", title: "Vous commandez", desc: "Choisissez votre formule et renseignez les détails de votre événement en quelques minutes." },
    { n: "02", title: "Nous créons", desc: "Une page et un QR code sur-mesure sont générés automatiquement pour votre célébration." },
    { n: "03", title: "Vos invités scannent", desc: "Le jour J, ils scannent et partagent photos, vidéos et messages vocaux instantanément." },
    { n: "04", title: "Vous conservez", desc: "Accédez à votre album et livre audio complets, pour toujours." },
  ];

  const C = {
    bg: "#FAF8F5",
    gold: "#C8B89A",
    goldHover: "#A8977A",
    text: "#2C2A25",
    mid: "#4A4845",
    light: "#6B6864",
    white: "#FFFFFF",
    border: "rgba(200,184,154,0.28)",
  };

  const btnBase = {
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 400,
    fontSize: "0.65rem",
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    cursor: "pointer",
    border: "none",
    transition: "all 0.25s",
    display: "inline-block",
  };

  const navLinks = [
    { label: "Le concept", href: "#concept" },
    { label: "Comment ça marche", href: "#comment-ca-marche" },
    { label: "Tarifs", href: "#tarifs" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: "'Montserrat', sans-serif", color: C.text, overflowX: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=Montserrat:wght@300;400;500&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
        html{scroll-behavior:smooth}
        ::-webkit-scrollbar{width:4px}
        ::-webkit-scrollbar-track{background:#FAF8F5}
        ::-webkit-scrollbar-thumb{background:#C8B89A;border-radius:2px}
        @keyframes fadeUpL{from{opacity:0;transform:translateY(28px)}to{opacity:1;transform:translateY(0)}}
        @keyframes fadeInL{from{opacity:0}to{opacity:1}}
      `}</style>

      {/* NAV */}
      <nav ref={navRef} style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        padding: isMobile ? "16px 24px" : "20px 60px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        background: scrolled || navOpen ? "rgba(250,248,245,0.96)" : "transparent",
        backdropFilter: scrolled || navOpen ? "blur(14px)" : "none",
        borderBottom: scrolled || navOpen ? `1px solid ${C.border}` : "none",
        transition: "all 0.4s",
      }}>
        <img src="/logo.png" alt="Memories by Inès" style={{ height: 52, objectFit: "contain" }} />

        {isMobile ? (
          <button onClick={() => setNavOpen(o => !o)} aria-label="Menu" style={{ background: "none", border: "none", cursor: "pointer", display: "flex", flexDirection: "column", gap: 5, padding: 4 }}>
            <span style={{ display: "block", width: 22, height: 1, background: C.text, transition: "transform 0.25s", transform: navOpen ? "translateY(6px) rotate(45deg)" : "none" }} />
            <span style={{ display: "block", width: 22, height: 1, background: C.text, opacity: navOpen ? 0 : 1, transition: "opacity 0.2s" }} />
            <span style={{ display: "block", width: 22, height: 1, background: C.text, transition: "transform 0.25s", transform: navOpen ? "translateY(-6px) rotate(-45deg)" : "none" }} />
          </button>
        ) : (
          <div style={{ display: "flex", gap: 36, alignItems: "center" }}>
            {navLinks.map(({ label, href }) => (
              <a key={href} href={href} style={{ fontWeight: 400, fontSize: "0.75rem", letterSpacing: "0.14em", textTransform: "uppercase", color: C.mid, textDecoration: "none", cursor: "pointer", transition: "color 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.color = C.gold}
                onMouseLeave={e => e.currentTarget.style.color = C.mid}
              >{label}</a>
            ))}
            <button onClick={onDashboard} style={{ ...btnBase, background: "transparent", color: C.text, border: `1px solid ${C.gold}`, padding: "8px 20px" }}
              onMouseEnter={e => { e.currentTarget.style.background = C.gold; e.currentTarget.style.color = C.white; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = C.text; }}
            >Admin</button>
            <button onClick={() => onCheckout("prestige")} style={{ ...btnBase, background: C.gold, color: C.white, padding: "8px 22px" }}
              onMouseEnter={e => e.currentTarget.style.background = C.goldHover}
              onMouseLeave={e => e.currentTarget.style.background = C.gold}
            >Commencer</button>
          </div>
        )}
      </nav>

      {isMobile && navOpen && (
        <div style={{ position: "fixed", top: navHeight, left: 0, right: 0, zIndex: 99, background: "rgba(250,248,245,0.98)", backdropFilter: "blur(14px)", borderBottom: `1px solid ${C.border}`, padding: "8px 24px 28px", display: "flex", flexDirection: "column", animation: "fadeInL 0.22s ease" }}>
          {navLinks.map(({ label, href }) => (
            <a key={href} href={href} onClick={() => setNavOpen(false)} style={{ padding: "14px 0", fontWeight: 400, fontSize: "0.85rem", letterSpacing: "0.15em", textTransform: "uppercase", color: C.mid, textDecoration: "none", cursor: "pointer", borderBottom: `1px solid ${C.border}` }}>{label}</a>
          ))}
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 20 }}>
            <button onClick={() => { setNavOpen(false); onDashboard(); }} style={{ ...btnBase, background: "transparent", color: C.text, border: `1px solid ${C.gold}`, padding: "12px 0", width: "100%" }}>Admin</button>
            <button onClick={() => { setNavOpen(false); onCheckout("prestige"); }} style={{ ...btnBase, background: C.gold, color: C.white, padding: "12px 0", width: "100%" }}>Commencer</button>
          </div>
        </div>
      )}

      {/* HERO */}
      <div id="concept" style={{ minHeight: "100vh", position: "relative", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "url(/images/maries.jpeg)", backgroundSize: "cover", backgroundPosition: "center top" }} />
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)" }} />
        <div style={{ position: "relative", textAlign: "center", padding: "150px 24px 90px", maxWidth: 860, animation: "fadeUpL 1.1s ease forwards" }}>
          <div style={{ fontWeight: 400, fontSize: "0.75rem", letterSpacing: "0.35em", textTransform: "uppercase", color: C.gold, marginBottom: 28, textShadow: "0 1px 3px rgba(0,0,0,0.6), 0 2px 8px rgba(0,0,0,0.4)" }}>
            Album photo · Livre audio · QR code sur-mesure
          </div>
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(4rem,8vw,6.5rem)", fontWeight: 500, fontStyle: "italic", lineHeight: 1.08, color: "#FAF8F5", marginBottom: 24, textShadow: "0 1px 3px rgba(0,0,0,0.6), 0 2px 8px rgba(0,0,0,0.4)" }}>
            Chaque moment<br />mérite d&apos;être <span style={{ color: C.gold }}>immortalisé</span>
          </h1>
          <p style={{ fontWeight: 400, fontSize: "clamp(1.2rem,2vw,1.35rem)", color: "#FAF8F5", lineHeight: 1.85, marginBottom: 48, maxWidth: 500, marginLeft: "auto", marginRight: "auto", textShadow: "0 1px 3px rgba(0,0,0,0.6), 0 2px 8px rgba(0,0,0,0.4)" }}>
            Un QR code unique. Une page sur-mesure. Des souvenirs partagés, pour toujours.
          </p>
          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={() => onCheckout("prestige")} style={{ ...btnBase, background: C.gold, color: "#2C2A25", padding: "14px 38px" }}
              onMouseEnter={e => e.currentTarget.style.background = C.goldHover}
              onMouseLeave={e => e.currentTarget.style.background = C.gold}
            >Créer mon événement</button>
            <button onClick={onDemo} style={{ ...btnBase, background: "transparent", color: "#FAF8F5", border: "1px solid rgba(250,248,245,0.5)", padding: "14px 38px" }}
              onMouseEnter={e => { e.currentTarget.style.background = C.gold; e.currentTarget.style.color = "#2C2A25"; e.currentTarget.style.borderColor = C.gold; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#FAF8F5"; e.currentTarget.style.borderColor = "rgba(250,248,245,0.5)"; }}
            >Voir la démo</button>
          </div>
        </div>
        {/* scroll hint */}
        <div style={{ position: "absolute", bottom: 36, left: "50%", transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, opacity: 0.6 }}>
          <div style={{ width: 1, height: 48, background: `linear-gradient(to bottom, transparent, ${C.gold})` }} />
          <div style={{ fontWeight: 400, fontSize: "0.72rem", letterSpacing: "0.25em", textTransform: "uppercase", color: "rgba(250,248,245,0.7)" }}>Découvrir</div>
        </div>
      </div>

      {/* STEPS */}
      <div id="comment-ca-marche" style={{ background: C.white, padding: "110px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 80 }}>
            <div style={{ fontWeight: 300, fontSize: "0.58rem", letterSpacing: "0.32em", textTransform: "uppercase", color: C.gold, marginBottom: 18 }}>Comment ça marche</div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2rem,4vw,3rem)", fontWeight: 300, fontStyle: "italic", color: C.text }}>
              Simple. <span style={{ color: C.gold }}>Élégant.</span> Inoubliable.
            </h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: "1px", background: C.border }}>
            {steps.map(s => (
              <div key={s.n} style={{ background: C.white, padding: "44px 32px", transition: "background 0.3s" }}
                onMouseEnter={e => e.currentTarget.style.background = "#FAF8F5"}
                onMouseLeave={e => e.currentTarget.style.background = C.white}
              >
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "4.5rem", fontWeight: 300, fontStyle: "italic", color: `${C.gold}30`, lineHeight: 1, marginBottom: 20 }}>{s.n}</div>
                <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.25rem", fontWeight: 400, color: C.text, marginBottom: 12 }}>{s.title}</h3>
                <p style={{ fontWeight: 400, fontSize: "1rem", lineHeight: 1.85, color: C.mid }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* GALERIE */}
      <div style={{ background: C.bg, padding: "0 0 110px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 3 }}>
          {[
            { label: "Mariage",      img: "https://images.unsplash.com/photo-1606800052052-a08af7148866?w=600&q=80", pos: "center" },
            { label: "Anniversaire", img: "/images/gateau.webp",                                                       pos: "center 30%" },
            { label: "Naissance",    img: "https://images.unsplash.com/photo-1555252333-9f8e92e65df9?w=600&q=80",    pos: "center" },
          ].map(c => (
            <div key={c.label} style={{ position: "relative", aspectRatio: "3/4", overflow: "hidden" }}>
              <img
                src={c.img}
                alt={c.label}
                style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: c.pos, display: "block", transition: "transform 0.6s ease" }}
                onMouseEnter={e => e.currentTarget.style.transform = "scale(1.04)"}
                onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
              />
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(44,35,24,0.55) 0%, transparent 55%)" }} />
              <div style={{ position: "absolute", bottom: 24, left: 0, right: 0, textAlign: "center" }}>
                <span style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontSize: "clamp(1rem,2.5vw,1.4rem)", fontWeight: 300, color: "#FAF8F5", letterSpacing: "0.04em" }}>{c.label}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* PRICING */}
      <div id="tarifs" style={{ background: C.bg, padding: "110px 24px" }}>
        <div style={{ maxWidth: 1020, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 80 }}>
            <div style={{ fontWeight: 300, fontSize: "0.58rem", letterSpacing: "0.32em", textTransform: "uppercase", color: C.gold, marginBottom: 18 }}>Nos formules</div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2rem,4vw,3rem)", fontWeight: 300, fontStyle: "italic", color: C.text }}>
              Des formules <span style={{ color: C.gold }}>adaptées</span>
            </h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 20 }}>
            {plans.map(p => (
              <div key={p.id}
                style={{ background: C.white, border: p.popular ? `1.5px solid ${C.gold}` : `1px solid ${C.border}`, padding: "48px 38px", position: "relative", transition: "transform 0.3s, box-shadow 0.3s" }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-5px)"; e.currentTarget.style.boxShadow = "0 16px 48px rgba(200,184,154,0.14)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
              >
                {p.popular && (
                  <div style={{ position: "absolute", top: 22, right: 22, fontWeight: 400, fontSize: "0.5rem", letterSpacing: "0.18em", textTransform: "uppercase", color: C.white, background: C.gold, padding: "3px 10px" }}>Populaire</div>
                )}
                <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.6rem", fontWeight: 400, color: C.text, marginBottom: 14 }}>{p.name}</h3>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "3.2rem", fontWeight: 300, fontStyle: "italic", color: C.text, lineHeight: 1 }}>
                  <sup style={{ fontSize: "1.1rem", fontStyle: "normal", verticalAlign: "super" }}>€</sup>{p.price}
                </div>
                <div style={{ fontWeight: 400, fontSize: "0.9rem", color: C.light, marginTop: 4, marginBottom: 28, letterSpacing: "0.05em" }}>par événement</div>
                <div style={{ width: 28, height: 1, background: C.gold, marginBottom: 28 }} />
                <ul style={{ listStyle: "none", marginBottom: 38 }}>
                  {p.features.map(f => (
                    <li key={f} style={{ fontWeight: 400, fontSize: "1rem", color: C.mid, padding: "9px 0", borderBottom: `1px solid ${C.border}`, display: "flex", gap: 12, alignItems: "flex-start" }}>
                      <span style={{ color: C.gold, flexShrink: 0, lineHeight: 1.6 }}>—</span>{f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => onCheckout(p.id)}
                  style={{ ...btnBase, width: "100%", padding: "13px 0", background: p.popular ? C.gold : "transparent", color: p.popular ? C.white : C.text, border: `1px solid ${C.gold}` }}
                  onMouseEnter={e => { e.currentTarget.style.background = p.popular ? C.goldHover : C.gold; e.currentTarget.style.color = C.white; }}
                  onMouseLeave={e => { e.currentTarget.style.background = p.popular ? C.gold : "transparent"; e.currentTarget.style.color = p.popular ? C.white : C.text; }}
                >Choisir {p.name}</button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* SUPPLÉMENTS */}
      <div style={{ background: C.white, borderTop: `1px solid ${C.border}`, padding: "80px 24px", textAlign: "center" }}>
        <div style={{ maxWidth: 560, margin: "0 auto" }}>
          <div style={{ fontWeight: 300, fontSize: "0.58rem", letterSpacing: "0.32em", textTransform: "uppercase", color: C.gold, marginBottom: 18 }}>Options</div>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontWeight: 300, fontSize: "clamp(2rem,4vw,3rem)", color: C.text, margin: "0 0 0.3rem" }}>
            Suppléments
          </h2>
          <div style={{ width: 60, height: 1, background: C.gold, margin: "0 auto 2.5rem" }} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem", marginBottom: "2rem" }}>
            <div style={{ background: C.bg, border: `0.5px solid ${C.border}`, borderRadius: 4, padding: "1.8rem 1.5rem" }}>
              <div style={{ fontSize: 22, marginBottom: "0.8rem" }}>🔑</div>
              <p style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontSize: 20, color: C.text, margin: "0 0 0.5rem", fontWeight: 400 }}>Clé USB gravée</p>
              <p style={{ fontSize: "0.85rem", fontWeight: 400, color: C.mid, margin: "0 0 1rem", lineHeight: 1.6 }}>Tous vos souvenirs réunis sur une clé USB personnalisée à votre événement.</p>
              <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: C.gold, fontWeight: 300 }}>15 €</p>
            </div>
            <div style={{ background: C.bg, border: `0.5px solid ${C.border}`, borderRadius: 4, padding: "1.8rem 1.5rem" }}>
              <div style={{ fontSize: 22, marginBottom: "0.8rem" }}>🪧</div>
              <p style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontSize: 20, color: C.text, margin: "0 0 0.5rem", fontWeight: 400 }}>Tableau de bienvenue</p>
              <p style={{ fontSize: "0.85rem", fontWeight: 400, color: C.mid, margin: "0 0 1rem", lineHeight: 1.6 }}>Un panneau élégant avec votre QR code et les détails de votre événement, à poser ou suspendre.</p>
              <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: C.gold, fontWeight: 300 }}>Sur devis</p>
            </div>
          </div>
          <p style={{ fontSize: "0.85rem", fontWeight: 400, color: C.light, lineHeight: 1.7 }}>
            Pour ajouter ces options à votre commande, contactez-nous après votre achat à{" "}
            <a href="mailto:contact@memoriesbyines.com" style={{ color: C.gold, textDecoration: "none" }}>contact@memoriesbyines.com</a>
          </p>
        </div>
      </div>

      {/* CTA */}
      <div style={{ background: C.white, borderTop: `1px solid ${C.border}`, padding: "110px 24px", textAlign: "center" }}>
        <div style={{ maxWidth: 560, margin: "0 auto" }}>
          <div style={{ fontWeight: 300, fontSize: "0.58rem", letterSpacing: "0.32em", textTransform: "uppercase", color: C.gold, marginBottom: 20 }}>Prêt à commencer ?</div>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2.2rem,5vw,3.5rem)", fontWeight: 300, fontStyle: "italic", color: C.text, lineHeight: 1.2, marginBottom: 20 }}>
            Créez le souvenir <span style={{ color: C.gold }}>parfait</span>
          </h2>
          <p style={{ fontWeight: 400, fontSize: "1rem", color: C.mid, lineHeight: 1.9, marginBottom: 48 }}>
            Contactez-nous pour créer la page de votre prochain événement. Réponse sous 24h.
          </p>
          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={() => onCheckout("prestige")} style={{ ...btnBase, background: C.gold, color: C.white, padding: "14px 38px" }}
              onMouseEnter={e => e.currentTarget.style.background = C.goldHover}
              onMouseLeave={e => e.currentTarget.style.background = C.gold}
            >Commencer maintenant</button>
            <button onClick={onDemo} style={{ ...btnBase, background: "transparent", color: C.text, border: `1px solid ${C.gold}`, padding: "14px 38px" }}
              onMouseEnter={e => { e.currentTarget.style.background = C.gold; e.currentTarget.style.color = C.white; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = C.text; }}
            >Voir la démo</button>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div style={{ background: C.bg, borderTop: `1px solid ${C.border}`, padding: "36px 60px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.1rem", fontWeight: 400, color: C.text }}>
          Memories <em style={{ fontStyle: "italic", color: C.gold }}>by Inès</em>
        </div>
        <div style={{ fontWeight: 400, fontSize: "0.85rem", color: C.light, letterSpacing: "0.04em" }}>contact@memoriesbyines.com</div>
        <div style={{ fontWeight: 400, fontSize: "0.8rem", color: C.light }}>© 2026 Memories by Inès</div>
      </div>
    </div>
  );
};

//                                                    
//   APP ROOT   ROUTEUR
//                                                    

//
//   PORTE D'ACCES : CODE INVITE / MARIES
//
const EventCodeGate = ({ event, slug, onAuthenticated }) => {
  const openAccess = event.openAccess === true;
  const [searchParams] = useSearchParams();
  const urlCode = searchParams.get("code") || "";
  const [code, setCode] = useState(urlCode);
  const [guestName, setGuestName] = useState("");
  const [needsNameOnly, setNeedsNameOnly] = useState(openAccess);
  const [autoChecking, setAutoChecking] = useState(!!urlCode);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const autoTriedRef = useRef(false);

  const submitCode = async (codeValue, nameValue) => {
    if (!openAccess && !codeValue.trim()) { setError("Entrez le code reçu."); return; }
    setLoading(true); setError("");
    try {
      const redeem = httpsCallable(functions, "redeemEventCode");
      const { data } = await redeem({ slug, code: codeValue.trim(), guestName: nameValue ? nameValue.trim() : undefined });
      await signInWithCustomToken(auth, data.token);
      const { claims } = await auth.currentUser.getIdTokenResult();
      onAuthenticated(claims.role, claims.guestKey || "", data.guestName || "");
    } catch (e) {
      if (e.code === "functions/failed-precondition") {
        setNeedsNameOnly(true);
      } else if (e.code === "functions/resource-exhausted") {
        setError("Trop de tentatives, réessayez dans quelques minutes.");
      } else {
        setError("Code invalide.");
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    if (urlCode && !autoTriedRef.current) {
      autoTriedRef.current = true;
      submitCode(urlCode, "").finally(() => setAutoChecking(false));
    } else {
      setAutoChecking(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (autoChecking) {
    return (
      <div style={{ minHeight: "100vh", background: S.dark, display: "flex", alignItems: "center", justifyContent: "center", color: S.textLight, fontSize: "0.75rem", letterSpacing: "0.2em", textTransform: "uppercase" }}>
        Vérification du code...
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: S.dark, padding: 24 }}>
      <div style={{ width: "100%", maxWidth: 400, textAlign: "center" }}>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.8rem", fontWeight: 300, color: S.cream, marginBottom: 4 }}>{event.name}</div>
        <div style={{ fontSize: "0.62rem", letterSpacing: "0.2em", textTransform: "uppercase", color: S.textLight, marginBottom: 36 }}>{event.date}</div>
        <Card style={{ padding: 36, textAlign: "left", position: "relative" }}>
          <div style={{ position: "absolute", top: -1, left: 40, right: 40, height: 2, background: `linear-gradient(90deg,transparent,${S.gold},transparent)` }} />
          {needsNameOnly ? (
            <>
              <div style={{ fontSize: "0.7rem", color: S.textLight, marginBottom: 20, textAlign: "center" }}>Encore une étape : indiquez votre prénom pour accéder à vos souvenirs.</div>
              <Input label="Votre prénom" value={guestName} onChange={e => setGuestName(e.target.value)} placeholder="Sophie, Antoine..." onKeyDown={e => e.key === "Enter" && submitCode(code, guestName)} />
              {error && <div style={{ fontSize: "0.7rem", color: "#E88A8A", marginTop: 12 }}>{error}</div>}
              <Btn onClick={() => submitCode(code, guestName)} disabled={loading} style={{ width: "100%", padding: 14, marginTop: 20 }}>
                {loading ? "Vérification..." : "Continuer"}
              </Btn>
            </>
          ) : (
            <>
              <div style={{ fontSize: "0.58rem", letterSpacing: "0.3em", textTransform: "uppercase", color: S.gold, marginBottom: 20, textAlign: "center" }}>Accès protégé</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <Input label="Code d'accès" value={code} onChange={e => setCode(e.target.value)} placeholder="123456" inputMode="numeric" maxLength={6} />
                <Input label="Votre prénom (si invité)" value={guestName} onChange={e => setGuestName(e.target.value)} placeholder="Sophie, Antoine..." onKeyDown={e => e.key === "Enter" && submitCode(code, guestName)} />
              </div>
              {error && <div style={{ fontSize: "0.7rem", color: "#E88A8A", marginTop: 16 }}>{error}</div>}
              <Btn onClick={() => submitCode(code, guestName)} disabled={loading} style={{ width: "100%", padding: 14, marginTop: 20 }}>
                {loading ? "Vérification..." : "Accéder"}
              </Btn>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

function EventPageRoute({ isAdmin }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [checkingSession, setCheckingSession] = useState(!isAdmin);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getDocs(query(collection(db, "events"), where("slug", "==", slug)))
      .then(snap => {
        if (!active) return;
        setEvent(snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() });
        setLoading(false);
      })
      .catch(() => { if (active) { setEvent(null); setLoading(false); } });
    return () => { active = false; };
  }, [slug]);

  useEffect(() => {
    if (isAdmin || !event) { setCheckingSession(false); return; }
    let active = true;
    setCheckingSession(true);
    const unsub = onAuthStateChanged(auth, async user => {
      if (!active) return;
      if (!user) { setSession(null); setCheckingSession(false); return; }
      try {
        const { claims } = await user.getIdTokenResult();
        if (claims.eventId === event.id && (claims.role === "owner" || claims.role === "guest")) {
          const guestName = claims.role === "guest" ? (localStorage.getItem(`guestName_${event.id}`) || "") : "";
          if (active) { setSession({ role: claims.role, authorKey: claims.guestKey || "", guestName }); setCheckingSession(false); }
          return;
        }
      } catch (e) { /* session invalide, on retombe sur la porte d'acces */ }
      if (active) { setSession(null); setCheckingSession(false); }
    });
    return () => { active = false; unsub(); };
  }, [isAdmin, event]);

  const handleAuthenticated = (role, authorKey, guestName) => {
    if (role === "guest" && event) localStorage.setItem(`guestName_${event.id}`, guestName || "");
    setSession({ role, authorKey, guestName });
  };

  if (loading || (!isAdmin && checkingSession)) {
    return <div style={{ minHeight: "100vh", background: S.dark, display: "flex", alignItems: "center", justifyContent: "center", color: S.textLight, fontSize: "0.75rem", letterSpacing: "0.2em", textTransform: "uppercase" }}>Chargement...</div>;
  }
  if (!event) {
    return (
      <div style={{ minHeight: "100vh", background: S.dark, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", padding: 24 }}>
        <div>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.8rem", color: S.cream, marginBottom: 12 }}>Événement introuvable</div>
          <div style={{ fontSize: "0.75rem", color: S.textLight }}>Vérifiez le lien ou le QR code utilisé.</div>
        </div>
      </div>
    );
  }

  // L'admin (claim reel) contourne toujours la porte d'acces, comme
  // aujourd'hui via "Voir la page" depuis le dashboard.
  if (isAdmin) {
    return <EventPage event={event} onBack={() => navigate("/dashboard")} role="owner" />;
  }

  if (!session) {
    return <EventCodeGate event={event} slug={slug} onAuthenticated={handleAuthenticated} />;
  }

  return (
    <EventPage
      event={event}
      onBack={() => navigate("/")}
      role={session.role}
      guestName={session.guestName}
      authorKey={session.authorKey}
    />
  );
}

export default function App() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async user => {
      if (!user) { setIsAdmin(false); setAuthLoading(false); return; }
      try {
        const { claims } = await user.getIdTokenResult();
        setIsAdmin(claims.admin === true);
      } catch (e) {
        setIsAdmin(false);
      }
      setAuthLoading(false);
    });
    return unsub;
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={
          <LandingPage
            onDemo={() => window.location.href = "/demo"}
            onDashboard={() => window.location.href = isAdmin ? "/dashboard" : "/login"}
            onCheckout={(plan) => window.location.href = `/checkout?plan=${plan}`}
          />
        } />
        <Route path="/login" element={
          <LoginPage onLogin={() => window.location.href = "/dashboard"} />
        } />
        <Route path="/dashboard" element={
          authLoading ? null : isAdmin
            ? <DashboardPage
                onLogout={() => signOut(auth).then(() => window.location.href = "/")}
                onPreviewEvent={(e) => window.location.href = `/event/${e.slug}`}
              />
            : <LoginPage onLogin={() => window.location.href = "/dashboard"} />
        } />
        <Route path="/event/:slug" element={<EventPageRoute isAdmin={isAdmin} />} />
        <Route path="/demo" element={<DemoPage />} />
        <Route path="/checkout" element={
          <CheckoutPage
            onBack={() => window.location.href = "/"}
          />
        } />
        <Route path="/confirmation/:sessionId" element={<ConfirmationPage />} />
      </Routes>
    </BrowserRouter>
  );
}
